import React, { useState, useEffect, useRef, useCallback } from "react";
import API_BASE from "./apiConfig";
import { useAppConfig } from "./AppContext";
import { ThemeProvider, useTheme, getT, getBrandColors } from "./ThemeContext";
import FuturisticBackground from "./FuturisticBackground";
import {
  FaUser, FaUserFriends, FaGraduationCap, FaCreditCard, FaFileSignature,
  FaCheckCircle, FaPen, FaTimes, FaChevronRight, FaFilter,
  FaLock, FaShieldAlt, FaTag, FaIdCard, FaEnvelope, FaPhone,
  FaSun, FaMoon, FaExclamationTriangle, FaMapMarkerAlt, FaReceipt,
  FaCalendarAlt, FaDollarSign, FaSwimmer, FaArrowLeft, FaStar,
} from "react-icons/fa";
import SixersLogo from "./Sixers_Logo.png";
import { useSort } from "./hooks/useSort";
import { estimateProration } from "./utils/proration";

const BASE      = API_BASE;
const ADMIN_FEE = 25.00; // one-time administrative fee
const SIG_BLUE = "#1d6fa5"; // signature ink colour — brand navy-toned blue

const DEFAULT_CYCLE = "quarterly";
const CYCLES = [
  { id:"monthly",     label:"Monthly",     short:"mo",  info:true  },
  { id:"quarterly",   label:"Quarterly",   short:"qtr", info:false },
  { id:"semi_annual", label:"Semi-Annual", short:"6mo", info:false },
  { id:"annual",      label:"Annual",      short:"yr",  info:false },
];
const feeKey      = { monthly:"fee_monthly", quarterly:"fee_quarterly", semi_annual:"fee_semi_annual", annual:"fee_annual" };
const discTypeKey = { monthly:"disc_monthly_type", quarterly:"disc_quarterly_type", semi_annual:"disc_semi_type", annual:"disc_annual_type" };
const discValKey  = { monthly:"disc_monthly_value", quarterly:"disc_quarterly_value", semi_annual:"disc_semi_value", annual:"disc_annual_value" };

const FALLBACK_ROLES = {
  "Batting":   ["Opening Batter","Middle Order Batter","Lower Order Batter"],
  "Bowling":   ["Fast Bowler","Medium Pacer","Spinner"],
  "All-Round": ["All-Rounder","Batting All-Rounder","Bowling All-Rounder"],
  "Keeping":   ["Wicket Keeper","Wicket Keeper Batter"],
};

// Error-key → ref-id mapping (order = scroll priority)
const ERROR_FIELD_ORDER = [
  "first_name","last_name","date_of_birth","gender","skill_level","playing_role",
  "parent_name","parent_email","parent_phone",
  "program_id","waiver","signature",
];

const fmtPhone  = raw => {
  const d = raw.replace(/\D/g,"").slice(0,10);
  if (d.length<=3) return d;
  if (d.length<=6) return `${d.slice(0,3)}-${d.slice(3)}`;
  return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
};

/* ── Signature canvas ──────────────────────────────────────────────────────── */
function SigCanvas({ onSign, onClear, hasError, T, canvasRef }) {
  const drawing = useRef(false);

  // Set canvas internal px to match CSS display size ONCE on mount
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const init = () => {
      const rect = cv.getBoundingClientRect();
      const dpr  = window.devicePixelRatio || 1;
      if (cv.width !== Math.round(rect.width * dpr)) {
        cv.width  = Math.round(rect.width  * dpr);
        cv.height = Math.round(rect.height * dpr);
        const ctx = cv.getContext("2d");
        ctx.scale(dpr, dpr);
      }
    };
    const raf = requestAnimationFrame(init);
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line

  const pt = (e, cv) => {
    const r = cv.getBoundingClientRect();
    const s = e.touches ? e.touches[0] : e;
    return { x: s.clientX - r.left, y: s.clientY - r.top };
  };

  const startDraw = e => {
    e.preventDefault();
    drawing.current = true;
    const cv = canvasRef.current; const ctx = cv.getContext("2d"); const p = pt(e, cv);
    ctx.strokeStyle = SIG_BLUE; ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const drawMove = e => {
    e.preventDefault();
    if (!drawing.current) return;
    const cv = canvasRef.current; const ctx = cv.getContext("2d"); const p = pt(e, cv);
    ctx.strokeStyle = SIG_BLUE; ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.lineTo(p.x, p.y); ctx.stroke();
  };
  const endDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (canvasRef.current) onSign(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const cv = canvasRef.current;
    cv.getContext("2d").clearRect(0, 0, cv.width, cv.height);
    onClear();
  };

  return (
    <div>
      <div style={{
        position:"relative",
        border: hasError ? "1.5px solid rgba(239,68,68,0.65)" : `1.5px solid ${SIG_BLUE}44`,
        borderRadius:12,
        background: T.isDark ? "rgba(0,0,0,0.30)" : "rgba(3,78,123,0.03)",
        overflow:"hidden",
        boxShadow: hasError ? `0 0 0 3px rgba(239,68,68,0.12)` : `0 0 0 3px ${SIG_BLUE}12`,
      }}>
        {/* Baseline dashed guide */}
        <div style={{
          position:"absolute", bottom:26, left:20, right:20,
          borderBottom:`1px dashed ${SIG_BLUE}30`, pointerEvents:"none",
        }}/>
        {/* "Sign here" watermark */}
        <div style={{
          position:"absolute", top:"50%", left:"50%",
          transform:"translate(-50%,-50%)",
          pointerEvents:"none", userSelect:"none",
          fontSize:14, fontStyle:"italic", whiteSpace:"nowrap",
          color: T.isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.07)",
        }}>
          Sign here
        </div>
        <canvas
          ref={canvasRef}
          onMouseDown={startDraw} onMouseMove={drawMove} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={drawMove} onTouchEnd={endDraw}
          style={{ display:"block", width:"100%", height:130, cursor:"crosshair", touchAction:"none" }}
        />
      </div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:6}}>
        <div style={{fontSize:11,color:T.textMuted}}>
          Draw your signature above using mouse or finger
        </div>
        <button type="button" onClick={clear}
          style={{background:"none",border:"none",cursor:"pointer",color:T.textMuted,
                  fontSize:11,display:"flex",alignItems:"center",gap:4,padding:0}}>
          <FaTimes size={10}/> Clear
        </button>
      </div>
    </div>
  );
}

/* ── Inner form ────────────────────────────────────────────────────────────── */
function PageShell({ subtitle, children, isDark, T, toggleTheme, logoSrc, orgName }) {
  return (
    <div style={{minHeight:"100dvh",position:"relative",fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
      <FuturisticBackground/>
      <style>{`select option{background:${isDark?"rgb(4,14,32)":"#fff"};color:${isDark?"rgba(255,255,255,0.88)":getBrandColors().blueDark}}input:focus,select:focus{box-shadow:0 0 0 3px ${isDark?"rgba(29,111,165,0.25)":"rgba(3,78,123,0.15)"};border-color:${SIG_BLUE}!important}`}</style>
      <div style={{maxWidth:980,margin:"0 auto",padding:"20px 24px",position:"relative",zIndex:1}}>
        <div style={{display:"flex",alignItems:"center",gap:18,padding:"16px 24px",marginBottom:16,
                     background:isDark?"rgba(2,13,24,0.78)":"rgba(255,255,255,0.86)",
                     backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",borderRadius:16,
                     border:isDark?"1px solid rgba(255,255,255,0.10)":"1px solid rgba(3,78,123,0.14)",
                     boxShadow:isDark?"0 8px 32px rgba(0,0,0,0.5)":"0 8px 32px rgba(3,78,123,0.12)"}}>
          <div style={{width:70,height:70,borderRadius:"50%",flexShrink:0,
                       border:isDark?"2.5px solid rgba(247,206,11,0.70)":"2.5px solid rgba(3,78,123,0.55)",
                       display:"flex",alignItems:"center",justifyContent:"center",
                       background:isDark?"rgba(255,255,255,0.06)":"rgba(255,255,255,0.92)"}}>
            <img src={logoSrc} alt={orgName} style={{width:54,objectFit:"contain",opacity:0.95}} onError={e=>{e.target.src=SixersLogo;}}/>
          </div>
          <div style={{flex:1,textAlign:"center"}}>
            <div style={{fontSize:20,fontWeight:900,color:isDark?getBrandColors().yellow:getBrandColors().blue,letterSpacing:"0.04em",lineHeight:1.1}}>{orgName}</div>
            <div style={{fontSize:12,color:T.textMuted,marginTop:4,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase"}}>{subtitle}</div>
          </div>
          <button type="button" onClick={toggleTheme}
            style={{display:"flex",alignItems:"center",gap:7,padding:"8px 14px",borderRadius:9,flexShrink:0,
                    background:isDark?"rgba(255,255,255,0.07)":"rgba(3,78,123,0.07)",
                    border:isDark?"1px solid rgba(255,255,255,0.14)":"1px solid rgba(3,78,123,0.14)",cursor:"pointer"}}>
            {isDark?<><FaSun size={14} color={getBrandColors().yellow}/><span style={{fontSize:12,fontWeight:700,color:getBrandColors().yellow,whiteSpace:"nowrap"}}>Light Mode</span></>
                  :<><FaMoon size={14} color={getBrandColors().blue}/><span style={{fontSize:12,fontWeight:700,color:getBrandColors().blue,whiteSpace:"nowrap"}}>Dark Mode</span></>}
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function RegistrationFormInner({ onSubmitted }) {
  const { config }              = useAppConfig();
  const { isDark, toggleTheme } = useTheme();
  const T                       = getT(isDark);

  // ── Registration type selector ───────────────────────────────────────────────
  const [registrationType, setRegistrationType] = useState(null); // null | "student" | "customer"

  // ── Customer registration state ──────────────────────────────────────────────
  const BLANK_CUSTOMER = {
    first_name:"", last_name:"", date_of_birth:"", gender:"",
    email:"", phone:"",
    street_address:"", city:"", state:"", zip_code:"",
    emergency_name:"", emergency_phone:"", emergency_relation:"",
    registration_purpose:"", registration_notes:"",
    waiver_agreed: false,
  };
  const [cForm,        setCForm]        = useState(BLANK_CUSTOMER);
  const [cErrors,      setCErrors]      = useState({});
  const [cSubmitting,  setCSubmitting]  = useState(false);
  const [cDone,        setCDone]        = useState(false);
  const [cRefNum,      setCRefNum]      = useState("");
  const [cSignature,   setCSignature]   = useState("");
  const cSigCanvasRef                   = useRef(null);
  const cStripeRef                      = useRef(null);
  const cCardElRef                      = useRef(null);
  const cStripeMountedRef               = useRef(false);
  const [cCardState,   setCCardState]   = useState({ complete: false, error: null, brand: "" });

  const setC = (k, v) => { setCForm(f => ({ ...f, [k]: v })); setCErrors(e => ({ ...e, [k]: "" })); };

  // ── Membership plans + booking types (fetched for customer form) ─────────────
  const [membershipPlans,   setMembershipPlans]   = useState([]);
  const [bookingTypes,      setBookingTypes]      = useState([]);
  const [bookingOptionType, setBookingOptionType] = useState("membership"); // "membership" | "individual"
  const [selectedPlanId,    setSelectedPlanId]    = useState(null);

  useEffect(() => {
    if (registrationType !== "customer") return;
    fetch(`${BASE}/lb/membership-plans`).then(r=>r.json()).then(d=>setMembershipPlans(d.plans||[])).catch(()=>{});
    fetch(`${BASE}/lb/booking-types`).then(r=>r.json()).then(d=>setBookingTypes(d.booking_types||[])).catch(()=>{});
  }, [registrationType]);

  const [programs,     setPrograms]     = useState([]);
  const [rolesByGroup, setRolesByGroup] = useState(FALLBACK_ROLES);
  const [waiverData,   setWaiverData]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [done,         setDone]         = useState(false);
  const [refNum,       setRefNum]       = useState("");
  const [errors,       setErrors]       = useState({});
  const [levelFilter,  setLevelFilter]  = useState("all");

  const [form, setForm] = useState({
    first_name:"", last_name:"", date_of_birth:"", gender:"",
    street_address:"", city:"", state:"", zip_code:"",
    skill_level:"", playing_role:"", cricclub_player_id:"",
    parent_name:"", parent_email:"", parent_phone:"",
    program_id:"", billing_cycle: DEFAULT_CYCLE,
    waiver_agreed: false,
  });
  const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

  const [cardState, setCardState] = useState({ complete: false, error: null, brand: "" });
  const stripeRef       = useRef(null);
  const cardElRef       = useRef(null);
  const stripeMountedRef = useRef(false);
  const [signature, setSignature] = useState("");

  // Field refs for error-focus (one per ERROR_FIELD_ORDER entry)
  const fieldRefs = useRef({});
  const setRef = useCallback((key) => (el) => { if (el) fieldRefs.current[key] = el; }, []);
  const sigCanvasRef = useRef(null);

  const levels = ["all", ...Array.from(new Set(programs.map(p=>p.level_name).filter(Boolean)))];

  useEffect(() => {
    if (!form.skill_level || programs.length===0) return;
    const match = levels.find(l=>l!=="all"&&l.toLowerCase()===form.skill_level.toLowerCase())
                ||levels.find(l=>l!=="all"&&(l.toLowerCase().includes(form.skill_level.toLowerCase())||form.skill_level.toLowerCase().includes(l.toLowerCase())));
    if (match) { setLevelFilter(match); setForm(f=>({...f,program_id:""})); }
  // eslint-disable-next-line
  }, [form.skill_level, programs.length]);

  const filteredProgramsUnsorted = levelFilter==="all" ? programs : programs.filter(p=>p.level_name===levelFilter);
  const { sorted: filteredPrograms, toggleSort: toggleProgramSort, sortArrow: programSortArrow } = useSort(filteredProgramsUnsorted, null);

  useEffect(() => {
    fetch(`${BASE}/registration/programs`).then(r=>r.json()).then(d=>setPrograms(d.programs||[])).finally(()=>setLoading(false));
    fetch(`${BASE}/students/playing-roles`).then(r=>r.json()).then(d=>{
      if(Array.isArray(d)&&d.length){
        const g=d.reduce((acc,r)=>{const grp=r.role_group||"Other";(acc[grp]=acc[grp]||[]).push(r.role_name||r);return acc;},{});
        setRolesByGroup(g);
      }
    }).catch(()=>{});
    fetch(`${BASE}/registration/waiver-content`).then(r=>r.json()).then(d=>setWaiverData(d)).catch(()=>{});
  }, []);

  // Mount Stripe card element — re-runs when dark mode changes to pick up correct colors
  useEffect(() => {
    if (!STRIPE_PK) return;
    // Unmount previous instance so we can re-create with new theme colors
    if (cardElRef.current) { try { cardElRef.current.unmount(); } catch(_){} cardElRef.current = null; stripeMountedRef.current = false; }
    let cancelled = false;
    const textColor     = isDark ? "#e2e8f0" : "#1a2840";
    const placeholderClr = isDark ? "rgba(148,163,184,0.6)" : "rgba(3,78,123,0.40)";
    import("@stripe/stripe-js").then(({ loadStripe }) => loadStripe(STRIPE_PK)).then(stripe => {
      if (cancelled || !stripe) return;
      stripeRef.current = stripe;
      const elements = stripe.elements();
      const card = elements.create("card", {
        style: {
          base: {
            fontSize: "15px",
            fontFamily: "'Inter', 'Segoe UI', sans-serif",
            color: textColor,
            "::placeholder": { color: placeholderClr },
          },
          invalid: { color: "#ef4444" },
        },
        hidePostalCode: true,
      });
      const el = document.getElementById("reg-stripe-card-element");
      if (el) {
        card.mount(el);
        cardElRef.current = card;
        stripeMountedRef.current = true;
        card.on("change", (evt) => {
          setCardState({ complete: evt.complete, error: evt.error?.message || null, brand: evt.brand || "" });
          if (evt.error) setErrors(e => ({ ...e, card: evt.error.message }));
          else setErrors(e => ({ ...e, card: "" }));
        });
      }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [isDark]);

  const set = (k,v) => { setForm(f=>({...f,[k]:v})); setErrors(e=>({...e,[k]:""})); };

  // Mount Stripe card element for customer form
  useEffect(() => {
    if (!STRIPE_PK || registrationType !== "customer") return;
    if (cCardElRef.current) { try { cCardElRef.current.unmount(); } catch(_){} cCardElRef.current = null; cStripeMountedRef.current = false; }
    let cancelled = false;
    const textColor      = isDark ? "#e2e8f0" : "#1a2840";
    const placeholderClr = isDark ? "rgba(148,163,184,0.6)" : "rgba(3,78,123,0.40)";
    import("@stripe/stripe-js").then(({ loadStripe }) => loadStripe(STRIPE_PK)).then(stripe => {
      if (cancelled || !stripe) return;
      cStripeRef.current = stripe;
      const elements = stripe.elements();
      const card = elements.create("card", {
        style: { base: { fontSize:"15px", fontFamily:"'Inter','Segoe UI',sans-serif", color: textColor, "::placeholder":{ color: placeholderClr } }, invalid:{ color:"#ef4444" } },
        hidePostalCode: true,
      });
      const el = document.getElementById("cust-stripe-card-element");
      if (el) {
        card.mount(el); cCardElRef.current = card; cStripeMountedRef.current = true;
        card.on("change", (evt) => {
          setCCardState({ complete: evt.complete, error: evt.error?.message || null, brand: evt.brand || "" });
          if (evt.error) setCErrors(e => ({ ...e, card: evt.error.message }));
          else setCErrors(e => ({ ...e, card: "" }));
        });
      }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [isDark, registrationType]);

  /* ── Customer validation ────────────────────────────────────────────────── */
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateCustomer = () => {
    const e = {};
    if (!cForm.first_name.trim())  e.first_name         = "First name is required";
    if (!cForm.last_name.trim())   e.last_name          = "Last name is required";
    if (!cForm.email.trim())       e.email              = "Email address is required";
    else if (!emailRegex.test(cForm.email.trim())) e.email = "Enter a valid email";
    if (!cForm.phone.trim())       e.phone              = "Phone number is required";
    if (bookingOptionType === "membership" && !selectedPlanId) e.plan_id = "Please select a membership plan";
    if (!cForm.waiver_agreed)      e.waiver             = "You must agree to the terms";
    if (!cSignature)               e.signature          = "Signature is required";
    setCErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCustomerSubmit = async ev => {
    ev.preventDefault();
    if (!validateCustomer()) return;
    setCSubmitting(true);

    // Tokenise card if Stripe is configured
    let stripe_pm_id = null, stripe_card_brand = null, stripe_card_last4 = null;
    let stripe_card_exp_month = null, stripe_card_exp_year = null;
    if (STRIPE_PK && cStripeRef.current && cCardElRef.current) {
      try {
        const { paymentMethod, error } = await cStripeRef.current.createPaymentMethod({
          type: "card", card: cCardElRef.current,
          billing_details: { name: `${cForm.first_name} ${cForm.last_name}`, email: cForm.email, phone: cForm.phone },
        });
        if (error) { setCErrors(e => ({ ...e, card: error.message })); setCSubmitting(false); return; }
        stripe_pm_id          = paymentMethod.id;
        stripe_card_brand     = paymentMethod.card?.brand     || null;
        stripe_card_last4     = paymentMethod.card?.last4     || null;
        stripe_card_exp_month = paymentMethod.card?.exp_month || null;
        stripe_card_exp_year  = paymentMethod.card?.exp_year  || null;
      } catch { setCErrors(e => ({ ...e, card: "Card tokenisation failed." })); setCSubmitting(false); return; }
    }

    try {
      const res = await fetch(`${BASE}/lb/clients`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...cForm,
          registration_purpose: bookingOptionType === "membership" ? "lane_membership" : "individual_booking",
          requested_plan_id: bookingOptionType === "membership" ? selectedPlanId : null,
          stripe_pm_id, stripe_card_brand, stripe_card_last4,
          stripe_card_exp_month, stripe_card_exp_year,
          waiver_signature: cSignature,
        }),
      });
      const data = await res.json();
      if (res.ok && data.client_id) {
        setCDone(true);
        setCRefNum(`CLT-${String(data.client_id).padStart(5,"0")}`);
      } else {
        setCErrors({ _global: data.detail || "Submission failed. Please try again." });
      }
    } catch { setCErrors({ _global: "Network error. Please try again." }); }
    finally  { setCSubmitting(false); }
  };

  /* ── Student validation + focus on first error ──────────────────────────── */
  const validate = () => {
    const e = {};
    if (!form.first_name.trim())  e.first_name    = "First name is required";
    if (!form.last_name.trim())   e.last_name     = "Last name is required";
    if (!form.date_of_birth)      e.date_of_birth = "Date of birth is required";
    if (!form.gender)             e.gender        = "Please select a gender";
    if (!form.parent_name.trim()) e.parent_name   = "Parent / guardian name is required";
    if (!form.parent_email.trim())
      e.parent_email = "Email address is required";
    else if (!emailRegex.test(form.parent_email.trim()))
      e.parent_email = "Enter a valid email (e.g. name@example.com)";
    // Phone mandatory
    if (!form.parent_phone.trim())
      e.parent_phone = "Phone number is required";
    else if (form.parent_phone.replace(/\D/g,"").length < 10)
      e.parent_phone = "Phone must be 10 digits (xxx-xxx-xxxx)";
    if (!form.program_id)         e.program_id    = "Please select a coaching program";
    if (!form.waiver_agreed)      e.waiver        = "You must read and agree to the waiver";
    if (!signature)               e.signature     = "Signature is required to submit";
    setErrors(e);

    // Scroll to and focus the first field with an error
    if (Object.keys(e).length > 0) {
      const firstKey = ERROR_FIELD_ORDER.find(k => e[k]);
      if (firstKey) {
        const el = firstKey === "signature"
          ? sigCanvasRef.current
          : fieldRefs.current[firstKey];
        if (el) {
          el.scrollIntoView({ behavior:"smooth", block:"center" });
          if (typeof el.focus === "function") el.focus();
        }
      }
    }
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async ev => {
    ev.preventDefault(); if (!validate()) return;
    setSubmitting(true);
    const prog = programs.find(p=>String(p.program_id)===String(form.program_id));

    // Tokenise card via Stripe (if configured and card element mounted)
    let stripe_pm_id = null, stripe_card_brand = null, stripe_card_last4 = null;
    let stripe_card_exp_month = null, stripe_card_exp_year = null;
    if (STRIPE_PK && stripeRef.current && cardElRef.current) {
      try {
        const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
          type: "card",
          card: cardElRef.current,
          billing_details: {
            name:  form.parent_name,
            email: form.parent_email,
            phone: form.parent_phone,
          },
        });
        if (error) {
          setErrors(e => ({ ...e, card: error.message }));
          setSubmitting(false);
          return;
        }
        stripe_pm_id          = paymentMethod.id;
        stripe_card_brand     = paymentMethod.card?.brand     || null;
        stripe_card_last4     = paymentMethod.card?.last4     || null;
        stripe_card_exp_month = paymentMethod.card?.exp_month || null;
        stripe_card_exp_year  = paymentMethod.card?.exp_year  || null;
      } catch (stripeErr) {
        setErrors(e => ({ ...e, card: "Card tokenisation failed. Please check card details." }));
        setSubmitting(false);
        return;
      }
    }

    try {
      const res  = await fetch(`${BASE}/registration/submit`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          ...form, program_name: prog?.program_name || "",
          waiver_signature: signature,
          stripe_pm_id, stripe_card_brand, stripe_card_last4,
          stripe_card_exp_month, stripe_card_exp_year,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setDone(true); setRefNum(`REG-${String(data.submission_id).padStart(5,"0")}`);
        if (onSubmitted) onSubmitted(data.submission_id);
      } else { setErrors({_global:data.message||"Submission failed. Please try again."}); }
    } catch { setErrors({_global:"Network error. Please try again."}); }
    finally  { setSubmitting(false); }
  };

  /* ── Pricing ──────────────────────────────────────────────────────────── */
  const calcPricing = (p, cycle) => {
    const grossFee = parseFloat(p[feeKey[cycle]]||0);
    if (!grossFee) return {grossFee:0,discAmt:0,discLabel:null,netFee:0,vsMonthlyPct:0};
    const dtype=(p[discTypeKey[cycle]]||"").toLowerCase();
    const dval=parseFloat(p[discValKey[cycle]]||0);
    let discAmt=0,discLabel=null;
    if(dtype==="pct"&&dval>0){discAmt=Math.round(grossFee*dval/100*100)/100;discLabel=`${dval}%`;}
    else if(dtype==="amt"&&dval>0){discAmt=dval;discLabel=`$${dval.toFixed(2)}`;}
    const netFee=Math.max(0,grossFee-discAmt);
    const months=cycle==="quarterly"?3:cycle==="semi_annual"?6:cycle==="annual"?12:1;
    const monthly=parseFloat(p.fee_monthly||0);
    const vsMonthlyPct=(monthly*months>0&&monthly*months>netFee)?Math.round(((monthly*months-netFee)/(monthly*months))*100):0;
    return {grossFee,discAmt,discLabel,netFee,vsMonthlyPct};
  };

  const logoSrc = config.logo_url || SixersLogo;
  const orgName = config.org_name || "Sixers Cricket Academy";
  const selProg = programs.find(p=>String(p.program_id)===String(form.program_id));

  /* ── Style helpers ────────────────────────────────────────────────────── */
  // Dark mode dropdowns: use an opaque background so option text is always visible
  const selBg = isDark ? "rgba(4,14,32,0.97)" : T.inputBg;
  const inp = {
    width:"100%", boxSizing:"border-box", padding:"10px 13px", borderRadius:9, fontSize:14,
    border:`1.5px solid ${T.inputBdr}`, background:T.inputBg, color:T.inputText,
    outline:"none", colorScheme:isDark?"dark":"light",
  };
  const sel = { ...inp, background:selBg }; // selects get opaque bg in dark
  const errInp = { ...inp, border:`1.5px solid ${T.red}99`, background:isDark?"rgba(239,68,68,0.07)":"rgba(239,68,68,0.04)" };
  const errSel = { ...sel, border:`1.5px solid ${T.red}99`, background:isDark?"rgba(239,68,68,0.10)":"rgba(239,68,68,0.04)" };
  const inpS  = k => errors[k] ? errInp : inp;
  const selS  = k => errors[k] ? errSel : sel;

  const lbl = (txt, req) => (
    <div style={{fontSize:11,fontWeight:700,color:T.textLabel,marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>
      {txt}{req && <span style={{color:T.red,marginLeft:3}}>*</span>}
    </div>
  );

  const errMsg = k => errors[k]
    ? <div style={{fontSize:11,color:T.red,marginTop:4,display:"flex",alignItems:"center",gap:5,
                   padding:"4px 8px",borderRadius:6,background:T.redBg,width:"fit-content"}}>
        <FaExclamationTriangle size={9}/>{errors[k]}
      </div>
    : null;

  const glassCard = accent => ({
    background:T.glass, backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)",
    borderTop:T.glassBdr,borderRight:T.glassBdr,borderBottom:T.glassBdr,borderLeft:`3px solid ${accent}`,
    borderRadius:14, padding:"20px 24px", marginBottom:14, boxShadow:T.glassSh,
  });

  // Section header with step number badge
  const SecHead = ({step, icon, title, sub, right}) => (
    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
      <div style={{position:"relative",flexShrink:0}}>
        <div style={{width:36,height:36,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",
                     background:isDark?"rgba(255,255,255,0.07)":"rgba(3,78,123,0.07)",border:T.glassBdr}}>
          {React.cloneElement(icon,{size:15,color:T.textSub})}
        </div>
        {step && (
          <div style={{position:"absolute",top:-5,right:-5,width:16,height:16,borderRadius:"50%",
                       background:getBrandColors().yellow,color:getBrandColors().blue,fontSize:9,fontWeight:900,
                       display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>
            {step}
          </div>
        )}
      </div>
      <div style={{flex:1}}>
        <div style={{fontSize:12,fontWeight:800,color:T.textPrimary,letterSpacing:"0.08em",textTransform:"uppercase"}}>{title}</div>
        {sub&&<div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{sub}</div>}
      </div>
      {right}
    </div>
  );

  /* ── Shared page shell ───────────────────────────────────────────────── */
  const logoSrc0 = config.logo_url || SixersLogo;
  const orgName0 = config.org_name || "Sixers Cricket Academy";
  // PageShell is defined outside component (see below) — pass context as props
  const pageShellProps = { isDark, T, toggleTheme, logoSrc: logoSrc0, orgName: orgName0 };

  /* ── Compact step-1 type selector bar (rendered at top of each form) ── */
  const TypeSelectorBar = () => (
    <div style={{
      display:"flex",alignItems:"center",gap:10,padding:"12px 18px",marginBottom:16,
      background:isDark?"rgba(2,13,24,0.78)":"rgba(255,255,255,0.86)",
      backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",
      borderRadius:12,
      border:isDark?"1px solid rgba(255,255,255,0.10)":"1px solid rgba(3,78,123,0.14)",
      boxShadow:T.glassSh, flexWrap:"wrap",
    }}>
      <span style={{fontSize:12,fontWeight:800,color:T.textMuted,textTransform:"uppercase",
                    letterSpacing:"0.07em",whiteSpace:"nowrap",marginRight:4}}>Step 1 — Register as:</span>
      {[
        { type:"student",  label:"Student (Coaching Program)", accent:getBrandColors().blue,    accentDark:getBrandColors().yellow },
        { type:"customer", label:"Lane Booking Customer",      accent:"#6366f1", accentDark:"#6366f1" },
      ].map(opt => {
        const active = (registrationType === opt.type) || (registrationType === null && opt.type === "student");
        const col = isDark ? opt.accentDark : opt.accent;
        return (
          <label key={opt.type}
            style={{display:"flex",alignItems:"center",gap:7,padding:"7px 14px",borderRadius:9,cursor:"pointer",
                    background: active ? (isDark?"rgba(255,255,255,0.09)":"rgba(3,78,123,0.08)") : "transparent",
                    border:`1.5px solid ${active ? col : "transparent"}`,transition:"all 0.15s",userSelect:"none"}}>
            <input type="radio" name="reg_type" value={opt.type}
              checked={active}
              onChange={() => setRegistrationType(opt.type)}
              style={{accentColor:col,width:14,height:14,flexShrink:0}}/>
            <span style={{fontSize:13,fontWeight:active?800:600,color:active?col:T.textMuted,whiteSpace:"nowrap"}}>
              {opt.label}
            </span>
          </label>
        );
      })}
    </div>
  );

  /* ── Customer success screen ────────────────────────────────────────────── */
  if (cDone) return (
    <PageShell subtitle="Customer Registration" {...pageShellProps}>
      <div style={{maxWidth:480,margin:"0 auto",paddingTop:16}}>
        <div style={{background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",
                     border:"1.5px solid rgba(99,102,241,0.30)",borderRadius:16,padding:"48px 36px",
                     boxShadow:T.glassSh,textAlign:"center"}}>
          <div style={{width:80,height:80,borderRadius:"50%",background:"rgba(99,102,241,0.12)",
                       border:"2px solid #6366f1",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 24px"}}>
            <FaCheckCircle size={40} color="#6366f1"/>
          </div>
          <div style={{fontSize:24,fontWeight:900,color:T.textPrimary,marginBottom:10}}>Registration Submitted!</div>
          <div style={{fontSize:14,color:T.textMuted,marginBottom:8}}>Your reference number is</div>
          <div style={{fontSize:28,fontWeight:900,color:getBrandColors().yellow,letterSpacing:"0.08em",marginBottom:20}}>{cRefNum}</div>
          <div style={{fontSize:13,color:T.textMuted,lineHeight:1.8,marginBottom:24}}>
            Our team will review your registration and contact you once your account is activated.
            You will be able to book lanes and facilities once approved.
          </div>
          <div style={{padding:"14px 18px",background:isDark?"rgba(255,255,255,0.04)":"rgba(3,78,123,0.05)",
                       borderRadius:10,border:T.glassBdr,fontSize:13,color:T.textMuted}}>
            Please bring this reference number to the front desk for your first visit.
          </div>
          <button type="button" onClick={() => { setCForm(BLANK_CUSTOMER); setCDone(false); setCSignature(""); setRegistrationType(null); }}
            style={{marginTop:20,padding:"12px 28px",borderRadius:10,border:"none",cursor:"pointer",
                    background:`linear-gradient(135deg,${getBrandColors().yellow},#e8b800)`,color:getBrandColors().blue,fontWeight:800,fontSize:14}}>
            Register Another
          </button>
        </div>
      </div>
    </PageShell>
  );

  /* ── Customer registration form ──────────────────────────────────────────── */
  const cErrCount = Object.keys(cErrors).filter(k => k !== "_global" && cErrors[k]).length;

  // Billing summary helper
  const ADMIN_FEE_CUSTOMER = 25.00;
  const selectedPlan = membershipPlans.find(p => p.plan_id === selectedPlanId) || null;
  const billingLines = (() => {
    if (bookingOptionType === "membership" && selectedPlan) {
      const price    = parseFloat(selectedPlan.price || 0);
      const tax      = parseFloat(selectedPlan.tax_rate || 0);
      const disc     = parseFloat(selectedPlan.member_discount_pct || 0);
      const discAmt  = Math.round(price * disc / 100 * 100) / 100;
      const netPrice = Math.max(0, price - discAmt);
      const taxAmt   = Math.round(netPrice * tax / 100 * 100) / 100;
      const validity = selectedPlan.validity_months || selectedPlan.duration_months || 1;
      const freq     = selectedPlan.billing_frequency || "monthly";
      const freqLabel = { once:"Monthly", monthly:"Monthly", quarterly:"Quarterly", annually:"Annual" }[freq] || "Monthly";
      const totalFirstPayment = netPrice + taxAmt + ADMIN_FEE_CUSTOMER;
      const recurringTotal    = netPrice + taxAmt;
      return {
        type: "membership",
        freq, freqLabel, validity,
        rows: [
          { label:`${selectedPlan.plan_name} membership`, sublabel:freqLabel, value:`$${price.toFixed(2)}` },
          ...(discAmt > 0 ? [{ label:`Member discount (${disc}%)`, value:`-$${discAmt.toFixed(2)}`, color:"#22c55e" }] : []),
          ...(taxAmt > 0 ? [{ label:`Tax (${tax}%)`, value:`+$${taxAmt.toFixed(2)}` }] : []),
          { label:"Administrative fee", sublabel:"One-time, collected at activation", value:`$${ADMIN_FEE_CUSTOMER.toFixed(2)}`, color:"#f59e0b", dividerBefore:true },
        ],
        recurringTotal,
        totalFirstPayment,
        note: `${validity} month${validity !== 1 ? "s" : ""} validity`,
        ...(selectedPlan.max_weekly_hours ? { extra:`Up to ${selectedPlan.max_weekly_hours}h/week included` } : {}),
      };
    }
    if (bookingOptionType === "individual") {
      const rows = bookingTypes.map(bt => ({
        label: `${bt.type_name}`,
        sublabel: bt.duration_label,
        value: `$${parseFloat(bt.non_member_price||0).toFixed(2)}`,
        memberValue: parseFloat(bt.member_price||0) > 0 ? `$${parseFloat(bt.member_price).toFixed(2)}` : null,
      }));
      return {
        type: "individual",
        rows,
        adminFee: ADMIN_FEE_CUSTOMER,
        note: "Charged per booking — card saved at registration",
      };
    }
    return null;
  })();

  if (registrationType === "customer") return (
    <PageShell subtitle="Lane Booking Customer Registration" {...pageShellProps}>
      <div>
        {TypeSelectorBar()}

        {cErrors._global && (
          <div style={{background:T.redBg,border:`1px solid ${T.red}44`,borderRadius:10,padding:"12px 18px",color:T.red,fontSize:14,marginBottom:14}}>
            {cErrors._global}
          </div>
        )}
        {cErrCount > 0 && (
          <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:14,padding:"12px 18px",borderRadius:10,background:T.redBg,border:`1px solid ${T.red}44`}}>
            <FaExclamationTriangle size={16} color={T.red} style={{flexShrink:0,marginTop:1}}/>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:T.red,marginBottom:3}}>{cErrCount} field{cErrCount>1?"s":""} need{cErrCount===1?"s":""} attention</div>
              <div style={{fontSize:12,color:T.red,opacity:0.8}}>Please fix the highlighted fields and try again.</div>
            </div>
          </div>
        )}

        <form onSubmit={handleCustomerSubmit} noValidate autoComplete="off">
          {/* ── 1. Booking Option ── */}
          <div style={{background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",
                       borderTop:T.glassBdr,borderRight:T.glassBdr,borderBottom:T.glassBdr,borderLeft:`3px solid #6366f1`,borderRadius:14,padding:"20px 24px",marginBottom:14,boxShadow:T.glassSh}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
              <div style={{position:"relative",flexShrink:0}}>
                <div style={{width:36,height:36,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",
                             background:isDark?"rgba(255,255,255,0.07)":"rgba(99,102,241,0.08)",border:T.glassBdr}}>
                  <FaStar size={15} color="#6366f1"/>
                </div>
                <div style={{position:"absolute",top:-5,right:-5,width:16,height:16,borderRadius:"50%",
                             background:getBrandColors().yellow,color:getBrandColors().blue,fontSize:9,fontWeight:900,
                             display:"flex",alignItems:"center",justifyContent:"center"}}>2</div>
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:800,color:T.textPrimary,letterSpacing:"0.08em",textTransform:"uppercase"}}>Booking Option</div>
                <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Choose how you'd like to book lanes at Sixers Cricket Academy</div>
              </div>
            </div>

            {/* Option toggle row */}
            <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
              {[
                { key:"membership", label:"Membership Plan", icon:<FaIdCard size={13}/>, color:"#6366f1", desc:"Monthly or fixed-term plan with included hours and member pricing" },
                { key:"individual", label:"Non-Member", icon:<FaCreditCard size={13}/>, color:"#0ea5e9", desc:"Book lanes as needed — pay per session at non-member rates. $25 admin fee at approval." },
              ].map(opt => {
                const active = bookingOptionType === opt.key;
                return (
                  <label key={opt.key}
                    style={{flex:1,minWidth:200,display:"flex",alignItems:"flex-start",gap:10,padding:"12px 16px",
                            borderRadius:10,cursor:"pointer",transition:"all 0.15s",
                            background: active ? `${opt.color}10` : isDark?"rgba(255,255,255,0.02)":"rgba(3,78,123,0.02)",
                            border:`1.5px solid ${active ? opt.color : T.glassBdr.replace("1px solid ","")}`}}>
                    <input type="radio" name="booking_option" value={opt.key} checked={active}
                      onChange={() => { setBookingOptionType(opt.key); setSelectedPlanId(null); setC("registration_purpose", opt.key === "membership" ? "lane_membership" : "individual_booking"); }}
                      style={{accentColor:opt.color,marginTop:2,flexShrink:0}}/>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                        <span style={{color:active?opt.color:T.textMuted}}>{opt.icon}</span>
                        <span style={{fontSize:13,fontWeight:800,color:active?opt.color:T.textSub}}>{opt.label}</span>
                      </div>
                      <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5}}>{opt.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Option 1: Membership plans */}
            {bookingOptionType === "membership" && (
              <div>
                <div style={{fontSize:11,fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>
                  Select a Membership Plan
                </div>
                {membershipPlans.length === 0 ? (
                  <div style={{padding:"14px 16px",borderRadius:9,background:isDark?"rgba(255,255,255,0.04)":"rgba(3,78,123,0.04)",
                               border:T.glassBdr,fontSize:13,color:T.textMuted,textAlign:"center"}}>
                    No membership plans configured yet — contact the academy for options.
                  </div>
                ) : (
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
                    {membershipPlans.map(plan => {
                      const active = selectedPlanId === plan.plan_id;
                      const price  = parseFloat(plan.price||0);
                      const disc   = parseFloat(plan.member_discount_pct||0);
                      const freq   = plan.billing_frequency || "monthly";
                      const freqPill = { once:"Monthly", monthly:"Monthly", quarterly:"Quarterly", annually:"Annual" }[freq] || "Monthly";
                      return (
                        <label key={plan.plan_id}
                          style={{display:"flex",flexDirection:"column",gap:6,padding:"14px 16px",borderRadius:12,
                                  cursor:"pointer",transition:"all 0.15s",position:"relative",
                                  background: active ? "rgba(99,102,241,0.10)" : isDark?"rgba(255,255,255,0.03)":"rgba(3,78,123,0.03)",
                                  border:`1.5px solid ${active?"#6366f1":cErrors.plan_id?T.red+"60":T.glassBdr.replace("1px solid ","")}`}}>
                          <input type="radio" name="plan_sel" value={plan.plan_id} checked={active}
                            onChange={() => { setSelectedPlanId(plan.plan_id); setC("registration_purpose","lane_membership"); setCErrors(e=>({...e,plan_id:""})); }}
                            style={{position:"absolute",top:12,right:12,accentColor:"#6366f1",width:14,height:14}}/>
                          <div style={{fontSize:14,fontWeight:800,color:active?"#6366f1":T.textPrimary,paddingRight:20}}>{plan.plan_name}</div>
                          {plan.description && <div style={{fontSize:11,color:T.textMuted,lineHeight:1.5}}>{plan.description}</div>}
                          <div style={{display:"flex",alignItems:"center",gap:6,marginTop:6,flexWrap:"wrap"}}>
                            <span style={{fontSize:16,fontWeight:900,color:active?"#6366f1":T.textPrimary}}>
                              ${price.toFixed(2)}
                            </span>
                            <span style={{fontSize:13,color:T.textMuted,fontWeight:600}}>— {freqPill}</span>
                            {plan.max_weekly_hours > 0 && (
                              <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:700,
                                            background:"rgba(34,197,94,0.10)",color:"#22c55e"}}>
                                {plan.max_weekly_hours}h/wk
                              </span>
                            )}
                            {disc > 0 && (
                              <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:700,
                                            background:"rgba(251,191,36,0.10)",color:getBrandColors().orange}}>
                                {disc}% off
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {cErrors.plan_id && (
                  <div style={{fontSize:11,color:T.red,marginTop:8,display:"flex",alignItems:"center",gap:5,padding:"4px 8px",borderRadius:6,background:T.redBg,width:"fit-content"}}>
                    <FaExclamationTriangle size={9}/>{cErrors.plan_id}
                  </div>
                )}
              </div>
            )}

            {/* Option 2: Individual booking types / pricing */}
            {bookingOptionType === "individual" && (
              <div>
                <div style={{fontSize:11,fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>
                  Lane Pricing — Pay Per Session
                </div>
                {bookingTypes.length === 0 ? (
                  <div style={{padding:"14px 16px",borderRadius:9,background:isDark?"rgba(255,255,255,0.04)":"rgba(3,78,123,0.04)",
                               border:T.glassBdr,fontSize:13,color:T.textMuted,textAlign:"center"}}>
                    No pricing configured yet — contact the academy for rates.
                  </div>
                ) : (
                  <div style={{borderRadius:10,overflow:"hidden",border:T.glassBdr}}>
                    {/* Header */}
                    <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:0,
                                 padding:"8px 14px",background:isDark?"rgba(14,165,233,0.10)":"rgba(14,165,233,0.06)",
                                 borderBottom:T.glassBdr}}>
                      {["Lane / Cage Type","Duration","Standard Rate","Member Rate"].map(h=>(
                        <div key={h} style={{fontSize:10,fontWeight:800,color:"#0ea5e9",textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</div>
                      ))}
                    </div>
                    {bookingTypes.map((bt,i)=>(
                      <div key={bt.type_id}
                        style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:0,padding:"10px 14px",
                                borderBottom:i<bookingTypes.length-1?T.glassBdr:"none",
                                background:i%2===0?(isDark?"rgba(255,255,255,0.02)":"rgba(3,78,123,0.01)"):"transparent"}}>
                        <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>{bt.type_name}</div>
                        <div style={{fontSize:12,color:T.textSub}}>{bt.duration_label}</div>
                        <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>${parseFloat(bt.non_member_price||0).toFixed(2)}</div>
                        <div style={{fontSize:12,color:"#22c55e",fontWeight:600}}>
                          {parseFloat(bt.member_price||0)>0 ? `$${parseFloat(bt.member_price).toFixed(2)}` : "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── 2. Personal Information ── */}
          {(() => {
            const cInp = k => ({ width:"100%",boxSizing:"border-box",padding:"10px 13px",borderRadius:9,fontSize:14,
              border:`1.5px solid ${cErrors[k]?T.red+"99":T.inputBdr}`,background:T.inputBg,color:T.inputText,outline:"none",
              colorScheme:isDark?"dark":"light" });
            const cSel = k => ({ ...cInp(k), background:isDark?"rgba(4,14,32,0.97)":T.inputBg });
            const cLbl = (txt, req) => (
              <div style={{fontSize:11,fontWeight:700,color:T.textLabel,marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>
                {txt}{req&&<span style={{color:T.red,marginLeft:3}}>*</span>}
              </div>
            );
            const cErr = k => cErrors[k]
              ? <div style={{fontSize:11,color:T.red,marginTop:4,display:"flex",alignItems:"center",gap:5,padding:"4px 8px",borderRadius:6,background:T.redBg,width:"fit-content"}}>
                  <FaExclamationTriangle size={9}/>{cErrors[k]}
                </div>
              : null;

            return (
              <>
                <div style={{background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",
                             borderTop:T.glassBdr,borderRight:T.glassBdr,borderBottom:T.glassBdr,borderLeft:`3px solid ${getBrandColors().yellow}`,borderRadius:14,padding:"20px 24px",marginBottom:14,boxShadow:T.glassSh}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                    <div style={{position:"relative",flexShrink:0}}>
                      <div style={{width:36,height:36,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",
                                   background:isDark?"rgba(255,255,255,0.07)":"rgba(3,78,123,0.07)",border:T.glassBdr}}>
                        <FaUser size={15} color={T.textSub}/>
                      </div>
                      <div style={{position:"absolute",top:-5,right:-5,width:16,height:16,borderRadius:"50%",
                                   background:getBrandColors().yellow,color:getBrandColors().blue,fontSize:9,fontWeight:900,
                                   display:"flex",alignItems:"center",justifyContent:"center"}}>2</div>
                    </div>
                    <div>
                      <div style={{fontSize:12,fontWeight:800,color:T.textPrimary,letterSpacing:"0.08em",textTransform:"uppercase"}}>Personal Information</div>
                      <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Your details as the account holder</div>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"12px 18px"}}>
                    <div>{cLbl("First Name",true)}<input value={cForm.first_name} onChange={e=>setC("first_name",e.target.value)} style={cInp("first_name")} placeholder="First name" autoComplete="off"/>{cErr("first_name")}</div>
                    <div>{cLbl("Last Name",true)}<input value={cForm.last_name} onChange={e=>setC("last_name",e.target.value)} style={cInp("last_name")} placeholder="Last name" autoComplete="off"/>{cErr("last_name")}</div>
                    <div>{cLbl("Date of Birth")}<input type="date" value={cForm.date_of_birth} onChange={e=>setC("date_of_birth",e.target.value)} style={cInp("date_of_birth")}/></div>
                    <div>
                      {cLbl("Gender")}
                      <select value={cForm.gender} onChange={e=>setC("gender",e.target.value)} style={cSel("gender")}>
                        <option value="">Select…</option>
                        <option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
                      </select>
                    </div>
                    <div>{cLbl("Email Address",true)}
                      <div style={{position:"relative"}}>
                        <FaEnvelope size={12} color={T.textMuted} style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",zIndex:1}}/>
                        <input type="text" inputMode="email" value={cForm.email} onChange={e=>setC("email",e.target.value)}
                          style={{...cInp("email"),paddingLeft:30}} placeholder="email@example.com" autoComplete="new-password"/>
                      </div>
                      {cErr("email")}
                    </div>
                    <div>{cLbl("Phone Number",true)}
                      <div style={{position:"relative"}}>
                        <FaPhone size={11} color={T.textMuted} style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",zIndex:1}}/>
                        <input type="text" inputMode="numeric" value={cForm.phone}
                          onChange={e=>setC("phone",fmtPhone(e.target.value))}
                          style={{...cInp("phone"),paddingLeft:30}} placeholder="xxx-xxx-xxxx" maxLength={12}/>
                      </div>
                      {cErr("phone")}
                    </div>
                  </div>
                  {/* Address */}
                  <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${T.divider}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:12}}>
                      <FaMapMarkerAlt size={12} color={T.textMuted}/>
                      <span style={{fontSize:11,fontWeight:800,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.05em"}}>Home Address</span>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"10px 18px"}}>
                      <div style={{gridColumn:"1/-1"}}>{cLbl("Street Address")}<input value={cForm.street_address} onChange={e=>setC("street_address",e.target.value)} style={cInp("street_address")} placeholder="123 Main St" autoComplete="off"/></div>
                      <div>{cLbl("City")}<input value={cForm.city} onChange={e=>setC("city",e.target.value)} style={cInp("city")} placeholder="City" autoComplete="off"/></div>
                      <div>{cLbl("State")}<input value={cForm.state} onChange={e=>setC("state",e.target.value)} style={cInp("state")} placeholder="State" autoComplete="off"/></div>
                      <div>{cLbl("Zip / Postal Code")}<input value={cForm.zip_code} onChange={e=>setC("zip_code",e.target.value)} style={cInp("zip_code")} placeholder="Zip code" autoComplete="off" maxLength={10}/></div>
                    </div>
                  </div>
                </div>

                {/* ── 3. Emergency Contact ── */}
                <div style={{background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",
                             borderTop:T.glassBdr,borderRight:T.glassBdr,borderBottom:T.glassBdr,borderLeft:"3px solid #a78bfa",borderRadius:14,padding:"20px 24px",marginBottom:14,boxShadow:T.glassSh}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                    <div style={{position:"relative",flexShrink:0}}>
                      <div style={{width:36,height:36,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",
                                   background:isDark?"rgba(255,255,255,0.07)":"rgba(167,139,250,0.08)",border:T.glassBdr}}>
                        <FaUserFriends size={15} color="#a78bfa"/>
                      </div>
                      <div style={{position:"absolute",top:-5,right:-5,width:16,height:16,borderRadius:"50%",
                                   background:getBrandColors().yellow,color:getBrandColors().blue,fontSize:9,fontWeight:900,
                                   display:"flex",alignItems:"center",justifyContent:"center"}}>3</div>
                    </div>
                    <div>
                      <div style={{fontSize:12,fontWeight:800,color:T.textPrimary,letterSpacing:"0.08em",textTransform:"uppercase"}}>Emergency Contact</div>
                      <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Person to contact in case of emergency</div>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"12px 18px"}}>
                    <div>{cLbl("Full Name")}<input value={cForm.emergency_name} onChange={e=>setC("emergency_name",e.target.value)} style={cInp("emergency_name")} placeholder="Emergency contact name" autoComplete="off"/></div>
                    <div>{cLbl("Phone")}<input value={cForm.emergency_phone} onChange={e=>setC("emergency_phone",fmtPhone(e.target.value))} style={cInp("emergency_phone")} placeholder="xxx-xxx-xxxx" maxLength={12}/></div>
                    <div>{cLbl("Relationship")}<input value={cForm.emergency_relation} onChange={e=>setC("emergency_relation",e.target.value)} style={cInp("emergency_relation")} placeholder="e.g. Spouse, Parent" autoComplete="off"/></div>
                  </div>
                </div>

                {/* ── 4. Billing Summary ── */}
                {billingLines && (
                  <div style={{background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",
                               borderTop:T.glassBdr,borderRight:T.glassBdr,borderBottom:T.glassBdr,borderLeft:"3px solid #f59e0b",borderRadius:14,padding:"20px 24px",marginBottom:14,boxShadow:T.glassSh}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                      <div style={{width:32,height:32,borderRadius:9,background:"rgba(245,158,11,0.12)",
                                   display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <FaReceipt size={14} color="#f59e0b"/>
                      </div>
                      <div>
                        <div style={{fontSize:12,fontWeight:800,color:T.textPrimary,textTransform:"uppercase",letterSpacing:"0.07em"}}>Billing Summary</div>
                        <div style={{fontSize:11,color:T.textMuted,marginTop:1}}>{billingLines.note}</div>
                      </div>
                    </div>
                    {billingLines.type === "membership" && (
                      <>
                        <div style={{borderRadius:10,overflow:"hidden",border:T.glassBdr,marginBottom:12}}>
                          {billingLines.rows.map((row,i) => (
                            <div key={i}>
                              {row.dividerBefore && <div style={{height:1,background:T.divider}}/>}
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",
                                           background:row.dividerBefore?(isDark?"rgba(245,158,11,0.06)":"rgba(245,158,11,0.04)"):(i%2===0?(isDark?"rgba(255,255,255,0.02)":"rgba(3,78,123,0.01)"):"transparent")}}>
                                <div>
                                  <div style={{fontSize:13,color:row.color||T.textSub,fontWeight:row.dividerBefore?700:500}}>{row.label}</div>
                                  {row.sublabel && <div style={{fontSize:11,color:T.textMuted,marginTop:1}}>{row.sublabel}</div>}
                                </div>
                                <span style={{fontSize:13,fontWeight:700,color:row.color||T.textPrimary,whiteSpace:"nowrap",marginLeft:16}}>{row.value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                          <div style={{padding:"12px 16px",borderRadius:10,background:isDark?"rgba(247,206,11,0.08)":"rgba(247,206,11,0.07)",border:"1.5px solid rgba(247,206,11,0.30)"}}>
                            <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>First Payment (at activation)</div>
                            <div style={{fontSize:22,fontWeight:900,color:getBrandColors().yellow}}>${billingLines.totalFirstPayment.toFixed(2)}</div>
                            <div style={{fontSize:11,color:T.textMuted,marginTop:3}}>Includes $25 admin fee</div>
                          </div>
                          <div style={{padding:"12px 16px",borderRadius:10,background:isDark?"rgba(99,102,241,0.08)":"rgba(99,102,241,0.06)",border:"1.5px solid rgba(99,102,241,0.25)"}}>
                            <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>Recurring ({billingLines.freqLabel || "Monthly"})</div>
                            <div style={{fontSize:22,fontWeight:900,color:"#6366f1"}}>${billingLines.recurringTotal.toFixed(2)}</div>
                            <div style={{fontSize:11,color:T.textMuted,marginTop:3}}>{billingLines.validity} month{billingLines.validity!==1?"s":""} validity</div>
                          </div>
                        </div>
                        {billingLines.extra && (
                          <div style={{padding:"8px 12px",borderRadius:8,background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.2)",fontSize:12,color:"#22c55e",fontWeight:600}}>
                            ✓ {billingLines.extra}
                          </div>
                        )}
                      </>
                    )}
                    {billingLines.type === "individual" && (
                      <>
                        {billingLines.rows.length > 0 && (
                          <div style={{borderRadius:10,overflow:"hidden",border:T.glassBdr,marginBottom:12}}>
                            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",padding:"8px 14px",background:isDark?"rgba(14,165,233,0.10)":"rgba(14,165,233,0.06)",borderBottom:T.glassBdr}}>
                              {["Lane / Type","Duration","Standard","Member"].map(h=>(
                                <div key={h} style={{fontSize:10,fontWeight:800,color:"#0ea5e9",textTransform:"uppercase",letterSpacing:"0.05em"}}>{h}</div>
                              ))}
                            </div>
                            {billingLines.rows.map((row,i)=>(
                              <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",padding:"9px 14px",borderBottom:i<billingLines.rows.length-1?T.glassBdr:"none",background:i%2===0?(isDark?"rgba(255,255,255,0.02)":"rgba(3,78,123,0.01)"):"transparent"}}>
                                <div style={{fontSize:13,fontWeight:600,color:T.textPrimary}}>{row.label}</div>
                                <div style={{fontSize:12,color:T.textMuted}}>{row.sublabel}</div>
                                <div style={{fontSize:13,fontWeight:700,color:T.textPrimary}}>{row.value}</div>
                                <div style={{fontSize:12,fontWeight:600,color:"#22c55e"}}>{row.memberValue||"—"}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                          <div style={{padding:"12px 16px",borderRadius:10,background:isDark?"rgba(245,158,11,0.08)":"rgba(245,158,11,0.06)",border:"1.5px solid rgba(245,158,11,0.30)"}}>
                            <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>Administrative Fee</div>
                            <div style={{fontSize:22,fontWeight:900,color:"#f59e0b"}}>${billingLines.adminFee.toFixed(2)}</div>
                            <div style={{fontSize:11,color:T.textMuted,marginTop:3}}>One-time · collected at activation</div>
                          </div>
                          <div style={{padding:"12px 16px",borderRadius:10,background:isDark?"rgba(14,165,233,0.08)":"rgba(14,165,233,0.05)",border:"1.5px solid rgba(14,165,233,0.25)"}}>
                            <div style={{fontSize:11,fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>Per Booking</div>
                            <div style={{fontSize:14,fontWeight:800,color:"#0ea5e9",marginBottom:3}}>Pay as you go</div>
                            <div style={{fontSize:11,color:T.textMuted}}>Card saved at registration — charged at time of each booking</div>
                          </div>
                        </div>
                        <div style={{padding:"8px 12px",borderRadius:8,background:"rgba(14,165,233,0.07)",border:"1px solid rgba(14,165,233,0.20)",fontSize:12,color:"#0ea5e9"}}>
                          Your card is stored securely via Stripe and charged only when you confirm a lane booking.
                        </div>
                      </>
                    )}
                    <div style={{marginTop:10,fontSize:11,color:T.textMuted,lineHeight:1.6,paddingTop:8,borderTop:`1px solid ${T.divider}`}}>
                      Payment is processed only after admin approval of your registration.
                    </div>
                  </div>
                )}

                {/* ── 5. Payment Profile ── */}
                <div style={{background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",
                             borderTop:T.glassBdr,borderRight:T.glassBdr,borderBottom:T.glassBdr,borderLeft:"3px solid #38bdf8",borderRadius:14,padding:"20px 24px",marginBottom:14,boxShadow:T.glassSh}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                    <div style={{position:"relative",flexShrink:0}}>
                      <div style={{width:36,height:36,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",
                                   background:isDark?"rgba(255,255,255,0.07)":"rgba(56,189,248,0.08)",border:T.glassBdr}}>
                        <FaCreditCard size={15} color="#38bdf8"/>
                      </div>
                      <div style={{position:"absolute",top:-5,right:-5,width:16,height:16,borderRadius:"50%",
                                   background:getBrandColors().yellow,color:getBrandColors().blue,fontSize:9,fontWeight:900,
                                   display:"flex",alignItems:"center",justifyContent:"center"}}>4</div>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:800,color:T.textPrimary,letterSpacing:"0.08em",textTransform:"uppercase"}}>Payment Profile</div>
                      <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Charged only after admin approval</div>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      {[{icon:<FaShieldAlt size={14} color={T.green}/>,label:"AES-256"},{icon:<FaLock size={14} color="#60a5fa"/>,label:"PCI DSS L1"},{icon:<FaIdCard size={14} color="#a78bfa"/>,label:"Zero Card Storage"}].map((b,i)=>(
                        <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"6px 10px",borderRadius:8,textAlign:"center",
                                             background:isDark?"rgba(255,255,255,0.06)":"rgba(3,78,123,0.05)",border:T.glassBdr}}>
                          {b.icon}
                          <div style={{fontSize:10,fontWeight:800,color:T.textSub,marginTop:4,whiteSpace:"nowrap"}}>{b.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {STRIPE_PK ? (
                    <>
                      <div style={{marginBottom:10,fontSize:12,color:T.textSub,lineHeight:1.6}}>
                        Enter your card details below. Your card will only be charged after admin approval.
                      </div>
                      <div id="cust-stripe-card-element" style={{padding:"12px 14px",borderRadius:8,
                        border:`1.5px solid ${cErrors.card?"#ef4444":cCardState.complete?"#22c55e":isDark?"rgba(56,189,248,0.35)":"#cbd5e1"}`,
                        background:isDark?"rgba(255,255,255,0.06)":"#fff",minHeight:42,transition:"border-color 0.2s"}}/>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6,minHeight:20}}>
                        {cErrors.card && <span style={{color:"#ef4444",fontSize:12,flex:1}}>{cErrors.card}</span>}
                        {!cErrors.card && cCardState.complete && (
                          <span style={{color:"#22c55e",fontSize:12,display:"flex",alignItems:"center",gap:4}}>
                            <FaCheckCircle size={12}/> Card details verified {cCardState.brand && <strong style={{textTransform:"capitalize",marginLeft:2}}>({cCardState.brand})</strong>}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div style={{padding:"14px 16px",borderRadius:10,background:"rgba(247,206,11,0.08)",border:"1px solid rgba(247,206,11,0.3)",fontSize:13,color:"#92400e",lineHeight:1.6}}>
                      <strong>Stripe not configured.</strong> Card capture is disabled. Registration can still be submitted — payment will be collected at approval.
                    </div>
                  )}
                  <div style={{fontSize:11,color:T.textMuted,marginTop:10,lineHeight:1.7}}>
                    Card details are transmitted directly to Stripe's servers and are <strong>never stored in our database</strong>. We only retain a secure token and display metadata. PCI DSS Level 1 certified.
                  </div>
                </div>

                {/* ── 5. Waiver & Signature ── */}
                <div style={{background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",
                             borderTop:T.glassBdr,borderRight:T.glassBdr,borderBottom:T.glassBdr,borderLeft:"3px solid #f87171",borderRadius:14,padding:"20px 24px",marginBottom:14,boxShadow:T.glassSh}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                    <div style={{position:"relative",flexShrink:0}}>
                      <div style={{width:36,height:36,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",
                                   background:isDark?"rgba(255,255,255,0.07)":"rgba(248,113,113,0.08)",border:T.glassBdr}}>
                        <FaFileSignature size={15} color={getBrandColors().red}/>
                      </div>
                      <div style={{position:"absolute",top:-5,right:-5,width:16,height:16,borderRadius:"50%",
                                   background:getBrandColors().yellow,color:getBrandColors().blue,fontSize:9,fontWeight:900,
                                   display:"flex",alignItems:"center",justifyContent:"center"}}>5</div>
                    </div>
                    <div>
                      <div style={{fontSize:12,fontWeight:800,color:T.textPrimary,letterSpacing:"0.08em",textTransform:"uppercase"}}>Waiver & Consent</div>
                      <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Read the content before signing digitally</div>
                    </div>
                  </div>
                  <div style={{height:300,overflowY:"auto",padding:"16px 20px",marginBottom:14,
                               background:isDark?"rgba(0,0,0,0.28)":"rgba(3,78,123,0.03)",border:T.glassBdr,borderRadius:10,fontSize:13,lineHeight:1.85,color:T.textSub}}>
                    {waiverData?(
                      <>
                        <div style={{fontSize:15,fontWeight:800,color:T.textPrimary,marginBottom:4}}>{waiverData.title||"General Liability Waiver"}</div>
                        {waiverData.version&&<div style={{fontSize:11,color:T.textMuted,marginBottom:12}}>Version: {waiverData.version}</div>}
                        {waiverData.intro&&<div style={{background:isDark?"rgba(247,206,11,0.07)":"rgba(247,206,11,0.10)",border:"1px solid rgba(247,206,11,0.2)",borderRadius:8,padding:"10px 14px",marginBottom:14,fontStyle:"italic",color:isDark?"rgba(247,206,11,0.85)":"#92400e",fontSize:12}}>{waiverData.intro}</div>}
                        {(waiverData.sections||[]).map((s,i)=>s.heading
                          ?<div key={i} style={{fontWeight:800,fontSize:13,color:T.textPrimary,marginTop:18,marginBottom:5,paddingBottom:4,borderBottom:`1px solid ${T.divider}`}}>{s.text}</div>
                          :<p key={i} style={{margin:"0 0 10px 0",color:T.textSub}}>{s.text}</p>)}
                      </>
                    ):<div style={{color:T.textMuted,textAlign:"center",paddingTop:40}}>Loading waiver content…</div>}
                  </div>
                  {/* Agreement checkbox */}
                  <label style={{display:"flex",alignItems:"flex-start",gap:13,cursor:"pointer",marginBottom:12,padding:"13px 16px",borderRadius:10,
                                 background:cForm.waiver_agreed?T.greenBg:isDark?"rgba(255,255,255,0.03)":"rgba(3,78,123,0.03)",
                                 border:`1.5px solid ${cForm.waiver_agreed?T.green+"55":cErrors.waiver?T.red+"60":T.glassBdr.replace("1px solid ","")}`,transition:"all 0.2s"}}>
                    <input type="checkbox" checked={cForm.waiver_agreed} onChange={e=>setC("waiver_agreed",e.target.checked)}
                      style={{marginTop:2,width:17,height:17,accentColor:T.green,flexShrink:0}}/>
                    <span style={{fontSize:13,color:T.textSub,lineHeight:1.7}}>
                      I, <strong style={{color:T.textPrimary}}>{cForm.first_name||"[Your Name]"} {cForm.last_name}</strong>,
                      have read and agree to all terms of the waiver above.
                      I confirm this electronic signature is legally binding.
                    </span>
                  </label>
                  {cErrors.waiver && <div style={{fontSize:11,color:T.red,marginTop:4,display:"flex",alignItems:"center",gap:5,padding:"4px 8px",borderRadius:6,background:T.redBg,width:"fit-content"}}><FaExclamationTriangle size={9}/>{cErrors.waiver}</div>}
                  {/* Signature pad */}
                  <div style={{marginTop:16}}>
                    <div style={{fontSize:11,fontWeight:700,color:T.textLabel,marginBottom:9,display:"flex",alignItems:"center",gap:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>
                      <FaPen size={10}/>
                      Signature{!cSignature&&<span style={{color:T.red}}>*</span>}
                      {cSignature&&<span style={{color:T.green,textTransform:"none",letterSpacing:0,fontWeight:700}}>✓ Signed</span>}
                      <span style={{fontWeight:400,textTransform:"none",color:T.textMuted,letterSpacing:0,fontSize:10}}>(Draw with mouse or finger · blue ink)</span>
                    </div>
                    <SigCanvas T={T} canvasRef={cSigCanvasRef}
                      onSign={data=>{setCSignature(data);setCErrors(e=>({...e,signature:""}));}}
                      onClear={()=>setCSignature("")} hasError={!!cErrors.signature}/>
                    {cErrors.signature&&<div style={{fontSize:11,color:T.red,marginTop:4,display:"flex",alignItems:"center",gap:5,padding:"4px 8px",borderRadius:6,background:T.redBg,width:"fit-content"}}><FaExclamationTriangle size={9}/>{cErrors.signature}</div>}
                  </div>
                </div>

                {/* Submit */}
                <button type="submit" disabled={cSubmitting}
                  style={{width:"100%",padding:"16px",border:"none",borderRadius:12,fontSize:15,fontWeight:800,
                          cursor:cSubmitting?"not-allowed":"pointer",
                          background:cSubmitting?isDark?"rgba(255,255,255,0.08)":"rgba(3,78,123,0.15)":`linear-gradient(135deg,${getBrandColors().yellow} 0%,#e8b800 100%)`,
                          color:cSubmitting?T.textMuted:getBrandColors().blue,display:"flex",alignItems:"center",justifyContent:"center",gap:10,
                          boxShadow:cSubmitting?"none":"0 4px 24px rgba(247,206,11,0.4)",marginTop:6,transition:"all 0.15s",letterSpacing:"0.04em"}}>
                  {cSubmitting?"Submitting…":<><FaChevronRight size={13}/> Submit Customer Registration</>}
                </button>
                <div style={{textAlign:"center",fontSize:11,color:T.textMuted,marginTop:10,marginBottom:8}}>
                  Your information is kept confidential and used only for academy records.
                </div>
              </>
            );
          })()}
        </form>
      </div>
    </PageShell>
  );

  /* ── Success screen ───────────────────────────────────────────────────── */
  if (done) return (
    <div style={{minHeight:"100dvh",display:"flex",alignItems:"center",justifyContent:"center",padding:24,position:"relative"}}>
      <FuturisticBackground/>
      <div style={{...glassCard(T.green),maxWidth:460,width:"100%",textAlign:"center",padding:"48px 36px",marginBottom:0}}>
        <div style={{width:80,height:80,borderRadius:"50%",background:isDark?"rgba(52,211,153,0.12)":"rgba(5,150,105,0.10)",
                     border:`2px solid ${T.green}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 24px"}}>
          <FaCheckCircle size={40} color={T.green}/>
        </div>
        <div style={{fontSize:24,fontWeight:900,color:T.textPrimary,marginBottom:10}}>Registration Submitted!</div>
        <div style={{fontSize:14,color:T.textMuted,marginBottom:8}}>Your reference number is</div>
        <div style={{fontSize:28,fontWeight:900,color:getBrandColors().yellow,letterSpacing:"0.08em",marginBottom:20}}>{refNum}</div>
        <div style={{fontSize:13,color:T.textMuted,lineHeight:1.8,marginBottom:24}}>
          Our team will review your registration and contact you once your student profile is activated.
        </div>
        <div style={{padding:"14px 18px",background:isDark?"rgba(255,255,255,0.04)":"rgba(3,78,123,0.05)",
                     borderRadius:10,border:T.glassBdr,fontSize:13,color:T.textMuted}}>
          Bring this reference number to the front desk for your first visit.
        </div>
      </div>
    </div>
  );

  const errorCount = Object.keys(errors).filter(k=>k!=="signature"&&k!=="waiver"&&errors[k]).length
                   + (errors.signature?1:0) + (errors.waiver?1:0);

  /* ── Form ─────────────────────────────────────────────────────────────── */
  return (
    <div style={{minHeight:"100dvh",position:"relative",fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
      <FuturisticBackground/>
      <style>{`
        @keyframes rf_haloY{0%,100%{box-shadow:0 0 0 4px rgba(247,206,11,0.12),0 0 14px 4px rgba(247,206,11,0.28);}50%{box-shadow:0 0 0 7px rgba(247,206,11,0.20),0 0 28px 8px rgba(247,206,11,0.52);}}
        @keyframes rf_haloB{0%,100%{box-shadow:0 0 0 4px rgba(3,78,123,0.10),0 0 14px 4px rgba(3,78,123,0.22);}50%{box-shadow:0 0 0 7px rgba(3,78,123,0.16),0 0 28px 8px rgba(3,78,123,0.40);}}
        select option { background: ${isDark ? "rgb(4,14,32)" : "#fff"}; color: ${isDark ? "rgba(255,255,255,0.88)" : getBrandColors().blueDark}; }
        select optgroup { background: ${isDark ? "rgb(4,14,32)" : "#fff"}; color: ${isDark ? "rgba(247,206,11,0.8)" : getBrandColors().blue}; }
        input:focus, select:focus { box-shadow: 0 0 0 3px ${isDark ? "rgba(29,111,165,0.25)" : "rgba(3,78,123,0.15)"}; border-color: ${SIG_BLUE} !important; }
      `}</style>

      <div style={{maxWidth:980,margin:"0 auto",padding:"20px 24px",position:"relative",zIndex:1}}>

        {/* ── Header ── */}
        <div style={{
          display:"flex",alignItems:"center",gap:18,padding:"16px 24px",marginBottom:16,
          background:isDark?"rgba(2,13,24,0.78)":"rgba(255,255,255,0.86)",
          backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",
          borderRadius:16,
          border:isDark?"1px solid rgba(255,255,255,0.10)":"1px solid rgba(3,78,123,0.14)",
          boxShadow:isDark?"0 8px 32px rgba(0,0,0,0.5)":"0 8px 32px rgba(3,78,123,0.12)",
        }}>
          {/* Logo with animated ring glow */}
          <div style={{
            width:70,height:70,borderRadius:"50%",flexShrink:0,
            border:isDark?`2.5px solid rgba(247,206,11,0.70)`:`2.5px solid rgba(3,78,123,0.55)`,
            display:"flex",alignItems:"center",justifyContent:"center",
            background:isDark?"rgba(255,255,255,0.06)":"rgba(255,255,255,0.92)",
            animation:isDark?"rf_haloY 2.6s ease-in-out infinite":"rf_haloB 2.6s ease-in-out infinite",
          }}>
            <img src={logoSrc} alt={orgName}
                 style={{width:54,objectFit:"contain",opacity:0.95}}
                 onError={e=>{e.target.src=SixersLogo;}}/>
          </div>
          {/* Title */}
          <div style={{flex:1,textAlign:"center"}}>
            <div style={{fontSize:20,fontWeight:900,color:isDark?getBrandColors().yellow:getBrandColors().blue,letterSpacing:"0.04em",lineHeight:1.1}}>{orgName}</div>
            <div style={{fontSize:12,color:T.textMuted,marginTop:4,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase"}}>
              Registration Form
            </div>
          </div>
          {/* Theme toggle with label */}
          <button type="button" onClick={toggleTheme}
            style={{display:"flex",alignItems:"center",gap:7,padding:"8px 14px",borderRadius:9,flexShrink:0,
                    background:isDark?"rgba(255,255,255,0.07)":"rgba(3,78,123,0.07)",
                    border:isDark?"1px solid rgba(255,255,255,0.14)":"1px solid rgba(3,78,123,0.14)",
                    cursor:"pointer"}}>
            {isDark
              ? <><FaSun  size={14} color={getBrandColors().yellow}/><span style={{fontSize:12,fontWeight:700,color:getBrandColors().yellow,whiteSpace:"nowrap"}}>Light Mode</span></>
              : <><FaMoon size={14} color={getBrandColors().blue}/><span style={{fontSize:12,fontWeight:700,color:getBrandColors().blue,whiteSpace:"nowrap"}}>Dark Mode</span></>}
          </button>
        </div>

        {/* ── Step 1: registration type selector ── */}
        {TypeSelectorBar()}

        {/* ── Validation error summary banner ── */}
        {errorCount > 0 && (
          <div style={{
            display:"flex",alignItems:"flex-start",gap:12,marginBottom:14,
            padding:"12px 18px",borderRadius:10,
            background:T.redBg,border:`1px solid ${T.red}44`,
          }}>
            <FaExclamationTriangle size={16} color={T.red} style={{flexShrink:0,marginTop:1}}/>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:T.red,marginBottom:3}}>
                {errorCount} field{errorCount>1?"s":""} need{errorCount===1?"s":""} attention
              </div>
              <div style={{fontSize:12,color:T.red,opacity:0.8}}>
                Please scroll down and fix the highlighted fields, then try submitting again.
              </div>
            </div>
          </div>
        )}

        {errors._global && (
          <div style={{background:T.redBg,border:`1px solid ${T.red}44`,borderRadius:10,
                       padding:"12px 18px",color:T.red,fontSize:14,marginBottom:14}}>
            {errors._global}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate autoComplete="off">
          <input type="text" name="fake_user" style={{display:"none"}} readOnly/>
          <input type="password" name="fake_pass" style={{display:"none"}} readOnly/>

          {/* ── 1. Student Information ── */}
          <div style={glassCard(getBrandColors().yellow)}>
            <SecHead step="1" icon={<FaUser/>} title="Student Information" sub="Child being enrolled"/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"12px 18px"}}>
              <div>
                {lbl("First Name",true)}
                <input ref={setRef("first_name")} value={form.first_name} onChange={e=>set("first_name",e.target.value)}
                  style={inpS("first_name")} placeholder="First name" autoComplete="off"/>
                {errMsg("first_name")}
              </div>
              <div>
                {lbl("Last Name",true)}
                <input ref={setRef("last_name")} value={form.last_name} onChange={e=>set("last_name",e.target.value)}
                  style={inpS("last_name")} placeholder="Last name" autoComplete="off"/>
                {errMsg("last_name")}
              </div>
              <div>
                {lbl("Date of Birth",true)}
                <input ref={setRef("date_of_birth")} type="date" value={form.date_of_birth}
                  onChange={e=>set("date_of_birth",e.target.value)} style={inpS("date_of_birth")}/>
                {errMsg("date_of_birth")}
              </div>
              <div>
                {lbl("Gender",true)}
                <select ref={setRef("gender")} value={form.gender} onChange={e=>set("gender",e.target.value)} style={selS("gender")}>
                  <option value="">Select…</option>
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
                {errMsg("gender")}
              </div>
              <div>
                {lbl("Skill Level")}
                <select ref={setRef("skill_level")} value={form.skill_level} onChange={e=>set("skill_level",e.target.value)} style={sel}>
                  <option value="">Select skill level…</option>
                  {levels.filter(l=>l!=="all").map(l=><option key={l} value={l}>{l}</option>)}
                </select>
                {form.skill_level&&levelFilter!=="all"&&(
                  <div style={{fontSize:11,color:T.green,marginTop:4}}>✓ Programs filtered to <strong>{levelFilter}</strong></div>
                )}
              </div>
              <div>
                {lbl("Tournament Playing Role")}
                <select ref={setRef("playing_role")} value={form.playing_role} onChange={e=>set("playing_role",e.target.value)} style={sel}>
                  <option value="">Select playing role…</option>
                  {Object.entries(rolesByGroup).map(([grp,roles])=>(
                    <optgroup key={grp} label={grp}>
                      {roles.map(r=><option key={r} value={r}>{r}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                {lbl("CricClub Player ID")}
                <input value={form.cricclub_player_id} onChange={e=>set("cricclub_player_id",e.target.value)}
                  style={inp} placeholder="e.g. CC-12345 (optional)" autoComplete="off"/>
                <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>
                  Find your CC ID at{" "}
                  <a href={config.external_league_url || "https://cricclubs.com"} target="_blank" rel="noopener noreferrer"
                     style={{color:isDark?"#60a5fa":getBrandColors().blue,textDecoration:"none",fontWeight:600}}>
                    {(config.external_league_url || "cricclubs.com").replace(/^https?:\/\//, "")}
                  </a>
                </div>
              </div>
            </div>

            {/* ── Address ── */}
            <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${T.divider}`}}>
              <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:12}}>
                <FaMapMarkerAlt size={12} color={T.textMuted}/>
                <span style={{fontSize:11,fontWeight:800,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.05em"}}>
                  Home Address
                </span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"10px 18px"}}>
                <div style={{gridColumn:"1/-1"}}>
                  {lbl("Street Address")}
                  <input value={form.street_address} onChange={e=>set("street_address",e.target.value)}
                    style={inp} placeholder="123 Main St" autoComplete="off"/>
                </div>
                <div>
                  {lbl("City")}
                  <input value={form.city} onChange={e=>set("city",e.target.value)}
                    style={inp} placeholder="City" autoComplete="off"/>
                </div>
                <div>
                  {lbl("State")}
                  <input value={form.state} onChange={e=>set("state",e.target.value)}
                    style={inp} placeholder="State" autoComplete="off"/>
                </div>
                <div>
                  {lbl("Zip / Postal Code")}
                  <input value={form.zip_code} onChange={e=>set("zip_code",e.target.value)}
                    style={inp} placeholder="Zip code" autoComplete="off" maxLength={10}/>
                </div>
              </div>
            </div>
          </div>

          {/* ── 2. Parent / Guardian ── */}
          <div style={glassCard("#a78bfa")}>
            <SecHead step="2" icon={<FaUserFriends/>} title="Parent / Guardian" sub="Primary contact person"/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"12px 18px"}}>
              <div>
                {lbl("Full Name",true)}
                <input ref={setRef("parent_name")} value={form.parent_name}
                  onChange={e=>set("parent_name",e.target.value)}
                  style={inpS("parent_name")} placeholder="Parent / guardian name" autoComplete="off"/>
                {errMsg("parent_name")}
              </div>
              <div>
                {lbl("Email Address",true)}
                <div style={{position:"relative"}}>
                  <FaEnvelope size={12} color={T.textMuted}
                    style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",zIndex:1}}/>
                  <input
                    ref={setRef("parent_email")}
                    type="text" inputMode="email" name="reg_email" autoComplete="new-password"
                    value={form.parent_email} onChange={e=>set("parent_email",e.target.value)}
                    style={{...inpS("parent_email"),paddingLeft:30}}
                    placeholder="email@example.com"/>
                </div>
                {errMsg("parent_email")}
              </div>
              <div>
                {lbl("Phone Number",true)}
                <div style={{position:"relative"}}>
                  <FaPhone size={11} color={T.textMuted}
                    style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",zIndex:1}}/>
                  <input
                    ref={setRef("parent_phone")}
                    type="text" inputMode="numeric"
                    value={form.parent_phone}
                    onChange={e=>set("parent_phone",fmtPhone(e.target.value))}
                    style={{...inpS("parent_phone"),paddingLeft:30}}
                    placeholder="xxx-xxx-xxxx" maxLength={12}/>
                </div>
                {errMsg("parent_phone")}
              </div>
            </div>
          </div>

          {/* ── 3. Coaching Program ── */}
          <div style={glassCard(getBrandColors().green)}>
            {/* Row 1: icon + title | billing pills */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <div style={{width:36,height:36,borderRadius:9,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
                           background:isDark?"rgba(255,255,255,0.07)":"rgba(3,78,123,0.07)",border:T.glassBdr,position:"relative"}}>
                <FaGraduationCap size={15} color={T.textSub}/>
                <div style={{position:"absolute",top:-5,right:-5,width:16,height:16,borderRadius:"50%",
                             background:getBrandColors().yellow,color:getBrandColors().blue,fontSize:9,fontWeight:900,
                             display:"flex",alignItems:"center",justifyContent:"center"}}>3</div>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:800,color:T.textPrimary,letterSpacing:"0.08em",textTransform:"uppercase"}}>Coaching Program</div>
                <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>Select a training program</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                <FaTag size={9} color={T.textMuted}/>
                {CYCLES.filter(c=>!c.info).map(c=>{
                  const active=form.billing_cycle===c.id;
                  return (
                    <button key={c.id} type="button" onClick={()=>set("billing_cycle",c.id)}
                      style={{padding:"5px 14px",borderRadius:20,fontSize:12,fontWeight:700,cursor:"pointer",
                              border:active?"none":T.glassBdr,
                              background:active?`linear-gradient(135deg,${getBrandColors().yellow},#e8b800)`:isDark?"rgba(255,255,255,0.06)":"rgba(3,78,123,0.06)",
                              color:active?getBrandColors().blue:T.textSub,
                              boxShadow:active?"0 2px 10px rgba(247,206,11,0.4)":"none",transition:"all 0.15s"}}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Row 2: Level filter pills */}
            {!loading&&levels.length>2&&(
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:14,
                           padding:"8px 12px",borderRadius:8,
                           background:isDark?"rgba(255,255,255,0.03)":"rgba(3,78,123,0.03)",border:T.glassBdr}}>
                <FaFilter size={9} color={T.textMuted}/>
                <span style={{fontSize:11,color:T.textMuted,fontWeight:600,marginRight:2}}>Filter by Level:</span>
                {levels.map(l=>{
                  const active=levelFilter===l;
                  const cnt=l==="all"?programs.length:programs.filter(p=>p.level_name===l).length;
                  return (
                    <button key={l} type="button" onClick={()=>{setLevelFilter(l);set("program_id","");}}
                      style={{padding:"4px 13px",borderRadius:20,fontSize:12,fontWeight:700,cursor:"pointer",
                              border:active?"none":T.glassBdr,
                              background:active?getBrandColors().yellow:isDark?"rgba(255,255,255,0.06)":"rgba(3,78,123,0.06)",
                              color:active?getBrandColors().blue:T.textSub,
                              boxShadow:active?"0 2px 8px rgba(247,206,11,0.35)":"none",transition:"all 0.14s"}}>
                      {l==="all"?"All":l}<span style={{opacity:0.6,fontSize:10,marginLeft:3}}>({cnt})</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div ref={setRef("program_id")}>
              {errors.program_id&&(
                <div style={{marginBottom:10}}>{errMsg("program_id")}</div>
              )}
            </div>

            {loading ? (
              <div style={{color:T.textMuted,fontSize:14,padding:"18px 0",textAlign:"center"}}>Loading programs…</div>
            ) : filteredPrograms.length===0 ? (
              <div style={{color:T.textMuted,fontSize:14,padding:"14px 0"}}>
                {programs.length===0?"No programs available. Please contact the front desk."
                  :`No programs at ${levelFilter} level. Try a different filter.`}
              </div>
            ) : (
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr>
                      <th style={{...thSt(T),width:36}}/>
                      <th onClick={()=>toggleProgramSort("program_name")} style={{...thSt(T),textAlign:"left",cursor:"pointer",userSelect:"none"}}>Program{programSortArrow("program_name")}</th>
                      <th onClick={()=>toggleProgramSort("level_name")} style={{...thSt(T),textAlign:"left",width:110,cursor:"pointer",userSelect:"none"}}>Level{programSortArrow("level_name")}</th>
                      <th onClick={()=>toggleProgramSort("days_per_week")} style={{...thSt(T),textAlign:"center",width:80,cursor:"pointer",userSelect:"none"}}>Sessions{programSortArrow("days_per_week")}</th>
                      <th onClick={()=>toggleProgramSort("fee_monthly")} style={{...thSt(T),textAlign:"right",width:100,opacity:0.4,cursor:"pointer",userSelect:"none"}}>Monthly{programSortArrow("fee_monthly")}<br/><span style={{fontWeight:400,fontSize:9}}>(ref)</span></th>
                      {CYCLES.filter(c=>!c.info).map(c=>(
                        <th key={c.id} onClick={()=>toggleProgramSort(feeKey[c.id])} style={{
                          ...thSt(T),textAlign:"right",width:120,cursor:"pointer",userSelect:"none",
                          background:form.billing_cycle===c.id?isDark?"rgba(247,206,11,0.10)":"rgba(247,206,11,0.12)":"transparent",
                          color:form.billing_cycle===c.id?isDark?getBrandColors().yellow:getBrandColors().blue:T.textMuted,
                        }}>
                          {c.label}{programSortArrow(feeKey[c.id])}
                          {form.billing_cycle===c.id&&<span style={{display:"block",fontSize:9,fontWeight:700,color:T.green,marginTop:2}}>◆ Selected</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPrograms.map((p,i)=>{
                      const sel2=String(form.program_id)===String(p.program_id);
                      const mono=calcPricing(p,"monthly");
                      const priceCol=cycleId=>{
                        const c=CYCLES.find(x=>x.id===cycleId);
                        const {grossFee,discAmt,netFee,vsMonthlyPct}=calcPricing(p,cycleId);
                        const isActive=form.billing_cycle===cycleId;
                        const hasDisc=discAmt>0;
                        if(!grossFee) return <td key={cycleId} style={{...tdSt(T),textAlign:"right",background:isActive?isDark?"rgba(247,206,11,0.05)":"rgba(247,206,11,0.07)":"transparent"}}><span style={{opacity:0.2}}>—</span></td>;
                        return(
                          <td key={cycleId} style={{...tdSt(T),textAlign:"right",verticalAlign:"middle",
                                background:isActive?isDark?"rgba(247,206,11,0.07)":"rgba(247,206,11,0.10)":"transparent"}}>
                            {hasDisc&&<div style={{fontSize:11,textDecoration:"line-through",lineHeight:1.3,color:T.textMuted}}>${grossFee.toFixed(2)}/{c.short}</div>}
                            <div style={{fontSize:isActive?17:13,fontWeight:800,lineHeight:1.2,
                                         color:isActive?(isDark?getBrandColors().yellow:getBrandColors().blue):(hasDisc?T.green:T.textPrimary)}}>
                              ${netFee.toFixed(2)}<span style={{fontSize:10,fontWeight:500,color:T.textMuted,marginLeft:2}}>/{c.short}</span>
                            </div>
                            {vsMonthlyPct>0&&<div style={{fontSize:10,fontWeight:800,color:T.green,marginTop:3,background:T.greenBg,borderRadius:4,padding:"1px 6px",display:"inline-block"}}>Save {vsMonthlyPct}%</div>}
                          </td>
                        );
                      };
                      return(
                        <tr key={p.program_id} onClick={()=>set("program_id",p.program_id)}
                          style={{cursor:"pointer",
                                  background:sel2?isDark?"rgba(247,206,11,0.06)":"rgba(247,206,11,0.08)":i%2===0?isDark?"rgba(255,255,255,0.01)":"rgba(3,78,123,0.01)":"transparent",
                                  outline:sel2?`1.5px solid rgba(247,206,11,0.4)`:"none",outlineOffset:-1,transition:"background 0.12s"}}>
                          <td style={{...tdSt(T),textAlign:"center"}}>
                            <div style={{width:18,height:18,borderRadius:"50%",margin:"0 auto",
                                         border:`2px solid ${sel2?getBrandColors().yellow:isDark?"rgba(255,255,255,0.25)":"rgba(3,78,123,0.3)"}`,
                                         background:sel2?getBrandColors().yellow:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                              {sel2&&<div style={{width:6,height:6,borderRadius:"50%",background:getBrandColors().blue}}/>}
                            </div>
                          </td>
                          <td style={tdSt(T)}>
                            <div style={{fontWeight:sel2?800:600,color:sel2?(isDark?getBrandColors().yellow:getBrandColors().blue):T.textPrimary,fontSize:13}}>{p.program_name}</div>
                            {p.days_per_week&&<div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{p.days_per_week}× per week</div>}
                          </td>
                          <td style={tdSt(T)}>
                            {p.level_name?<span style={{padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:700,background:`${p.level_color||"#64748b"}20`,color:p.level_color||T.textMuted}}>{p.level_name}</span>:"—"}
                          </td>
                          <td style={{...tdSt(T),textAlign:"center",color:T.textMuted}}>{p.days_per_week?`${p.days_per_week}/wk`:"—"}</td>
                          <td style={{...tdSt(T),textAlign:"right",opacity:0.4}}>{mono.grossFee?<span style={{fontSize:12}}>${mono.netFee.toFixed(2)}/mo</span>:"—"}</td>
                          {["quarterly","semi_annual","annual"].map(priceCol)}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

          </div>

          {/* ── 4. Billing Summary ── */}
          {selProg && (()=>{
            const today        = new Date();
            const monthNames   = ["January","February","March","April","May","June","July","August","September","October","November","December"];
            const curMonth     = monthNames[today.getMonth()];
            const nextMonthD   = new Date(today.getFullYear(), today.getMonth()+1, 1);
            const nextCycle    = `${monthNames[nextMonthD.getMonth()]} ${nextMonthD.getFullYear()}`;
            const cyc          = CYCLES.find(c=>c.id===form.billing_cycle);
            const { grossFee, discAmt, discLabel, netFee } = calcPricing(selProg, form.billing_cycle);
            const monthlyGross = parseFloat(selProg.fee_monthly||0);
            const monthlyDisc  = (()=>{ const dt=(selProg.disc_monthly_type||"").toLowerCase(); const dv=parseFloat(selProg.disc_monthly_value||0); if(dt==="pct"&&dv>0) return Math.round(monthlyGross*dv/100*100)/100; if(dt==="amt"&&dv>0) return dv; return 0; })();
            const monthlyNet   = Math.max(0, monthlyGross - monthlyDisc);
            const { daysInMonth, daysLeft, totalSessions, remainingSessions, prorated } =
              estimateProration(selProg.days_per_week, monthlyNet, today);
            const totalNow     = prorated + ADMIN_FEE;

            const Row = ({label, value, muted, bold, green, red, large, indent}) => (
              <div style={{display:"grid",gridTemplateColumns:"1fr auto",alignItems:"center",
                           padding:"5px 0",borderBottom:`1px solid ${T.divider}`,
                           ...(indent?{paddingLeft:14}:{})}}>
                <span style={{fontSize:muted?11:13,color:muted?T.textMuted:T.textSub,fontStyle:muted?"italic":"normal"}}>{label}</span>
                <span style={{fontSize:large?18:bold?14:12,fontWeight:bold||large?800:500,
                              color:green?T.green:red?"#ef4444":bold||large?(isDark?getBrandColors().yellow:getBrandColors().blue):T.textSub,
                              textAlign:"right"}}>{value}</span>
              </div>
            );

            return (
              <div style={glassCard("#f59e0b")}>
                <SecHead step="4" icon={<FaReceipt/>} title="Billing Summary"
                  sub={`What you'll be charged — transparent breakdown before you sign`}/>

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

                  {/* Left: First Payment */}
                  <div style={{padding:"14px 16px",borderRadius:10,
                               background:isDark?"rgba(245,158,11,0.08)":"rgba(245,158,11,0.07)",
                               border:`1px solid rgba(245,158,11,0.25)`}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12}}>
                      <FaCalendarAlt size={11} color="#f59e0b"/>
                      <span style={{fontSize:11,fontWeight:800,color:"#f59e0b",textTransform:"uppercase",letterSpacing:"0.05em"}}>
                        First Payment (Due Today)
                      </span>
                    </div>
                    <Row label={`Prorated — ${curMonth} (${remainingSessions}/${totalSessions} sessions @ $${monthlyNet.toFixed(2)}/mo)`}
                         value={`$${prorated.toFixed(2)}`}/>
                    <Row label="Administrative Fee (one-time)" value={`$${ADMIN_FEE.toFixed(2)}`}/>
                    <div style={{marginTop:10,padding:"10px 0",borderTop:`2px solid ${T.divider}`}}>
                      <Row label="Total Due (After Admin Approval)" value={`$${totalNow.toFixed(2)}`} bold large/>
                    </div>
                  </div>

                  {/* Right: Recurring */}
                  <div style={{padding:"14px 16px",borderRadius:10,
                               background:isDark?"rgba(52,211,153,0.06)":"rgba(52,211,153,0.06)",
                               border:`1px solid rgba(52,211,153,0.22)`}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12}}>
                      <FaDollarSign size={11} color={T.green}/>
                      <span style={{fontSize:11,fontWeight:800,color:T.green,textTransform:"uppercase",letterSpacing:"0.05em"}}>
                        Recurring — Starting {nextCycle}
                      </span>
                    </div>
                    <Row label={`${cyc?.label} Program Fee`} value={`$${grossFee.toFixed(2)}`}/>
                    {discAmt>0 && <Row label={`Discount (${discLabel})`} value={`-$${discAmt.toFixed(2)}`} red indent/>}
                    <div style={{marginTop:10,padding:"10px 0",borderTop:`2px solid ${T.divider}`}}>
                      <Row label={`Billed ${cyc?.label}`} value={`$${netFee.toFixed(2)}/${cyc?.short}`} bold large green/>
                    </div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:6,lineHeight:1.6}}>
                      Admin will configure your recurring schedule after batch assignment.
                    </div>
                  </div>
                </div>

                <div style={{marginTop:12,padding:"10px 14px",borderRadius:8,
                             background:isDark?"rgba(255,255,255,0.04)":"rgba(3,78,123,0.04)",
                             border:T.glassBdr,display:"flex",alignItems:"flex-start",gap:9}}>
                  <FaCheckCircle size={13} color={T.green} style={{flexShrink:0,marginTop:1}}/>
                  <div style={{fontSize:12,color:T.textMuted,lineHeight:1.7}}>
                    Your card will only be charged <strong style={{color:T.textSub}}>after Admin approval</strong>.
                    The prorated amount covers the rest of <strong style={{color:T.textSub}}>{curMonth}</strong>,
                    and the <strong style={{color:T.textSub}}>{cyc?.label} fee</strong> kicks in starting {nextCycle}.
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── 5. Payment Profile ── */}
          <div style={glassCard("#38bdf8")}>
            <SecHead step="5" icon={<FaCreditCard/>}
              title="Payment Profile"
              sub="Billed only after Admin approval and Batch assignment"
              right={
                <div style={{display:"flex",gap:8}}>
                  {[
                    {icon:<FaShieldAlt size={16} color={T.green}/>, label:"AES-256 Encryption",  sub:"Bank-grade secure"},
                    {icon:<FaLock      size={16} color="#60a5fa"/>, label:"PCI DSS Level 1",      sub:"Payment certified"},
                    {icon:<FaIdCard    size={16} color="#a78bfa"/>, label:"Zero Card Storage",    sub:"Never saved here"},
                  ].map((b,i)=>(
                    <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",
                                         padding:"8px 12px",borderRadius:10,minWidth:80,textAlign:"center",
                                         background:isDark?"rgba(255,255,255,0.06)":"rgba(3,78,123,0.05)",
                                         border:T.glassBdr}}>
                      {b.icon}
                      <div style={{fontSize:11,fontWeight:800,color:T.textSub,lineHeight:1.3,marginTop:5}}>{b.label}</div>
                      <div style={{fontSize:10,color:T.textMuted,lineHeight:1.3,marginTop:1}}>{b.sub}</div>
                    </div>
                  ))}
                </div>
              }
            />
            {STRIPE_PK ? (
              <>
                <div style={{marginBottom:10,fontSize:12,color:T.textSub,lineHeight:1.6}}>
                  Enter your card details below. Your card will only be charged after admin approval and batch assignment.
                </div>
                {/* Stripe-hosted card element — card number/CVV never touches our server */}
                <div id="reg-stripe-card-element" style={{
                  padding:"12px 14px", borderRadius:8,
                  border:`1.5px solid ${errors.card ? "#ef4444" : cardState.complete ? "#22c55e" : isDark ? "rgba(56,189,248,0.35)" : "#cbd5e1"}`,
                  background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                  minHeight:42,
                  transition:"border-color 0.2s",
                }}/>
                <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6,minHeight:20}}>
                  {errors.card && (
                    <span style={{color:"#ef4444",fontSize:12,flex:1}}>{errors.card}</span>
                  )}
                  {!errors.card && cardState.complete && (
                    <span style={{color:"#22c55e",fontSize:12,display:"flex",alignItems:"center",gap:4}}>
                      <FaCheckCircle size={12}/> Card details verified
                      {cardState.brand && <strong style={{textTransform:"capitalize",marginLeft:2}}>({cardState.brand})</strong>}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div style={{padding:"14px 16px",borderRadius:10,
                background:"rgba(247,206,11,0.08)",border:"1px solid rgba(247,206,11,0.3)",
                fontSize:13,color:"#92400e",lineHeight:1.6}}>
                <strong>Stripe not configured.</strong> Card capture is disabled. Registration can still be submitted — payment will be collected manually at approval.
              </div>
            )}
            <div style={{fontSize:11,color:T.textMuted,marginTop:10,lineHeight:1.7}}>
              Card details are transmitted directly to Stripe's servers and are <strong>never stored in our database</strong>. We only retain a secure token (payment method ID) and display metadata (card brand, last 4 digits, expiry). PCI DSS Level 1 certified.
            </div>
          </div>

          {/* ── 5. Waiver & Signature ── */}
          <div style={glassCard(getBrandColors().red)}>
            <SecHead step="6" icon={<FaFileSignature/>} title="Waiver & Consent"
              sub="Read the content before signing digitally."/>

            <div style={{
              height:380,overflowY:"auto",padding:"16px 20px",marginBottom:14,
              background:isDark?"rgba(0,0,0,0.28)":"rgba(3,78,123,0.03)",
              border:T.glassBdr,borderRadius:10,fontSize:13,lineHeight:1.85,color:T.textSub,
            }}>
              {waiverData?(
                <>
                  <div style={{fontSize:15,fontWeight:800,color:T.textPrimary,marginBottom:4}}>{waiverData.title||"General Liability Waiver"}</div>
                  {waiverData.version&&<div style={{fontSize:11,color:T.textMuted,marginBottom:12}}>Version: {waiverData.version}</div>}
                  {waiverData.intro&&(
                    <div style={{background:isDark?"rgba(247,206,11,0.07)":"rgba(247,206,11,0.10)",border:"1px solid rgba(247,206,11,0.2)",borderRadius:8,padding:"10px 14px",marginBottom:14,fontStyle:"italic",color:isDark?"rgba(247,206,11,0.85)":"#92400e",fontSize:12}}>{waiverData.intro}</div>
                  )}
                  {(waiverData.sections||[]).map((s,i)=>
                    s.heading
                      ? <div key={i} style={{fontWeight:800,fontSize:13,color:T.textPrimary,marginTop:18,marginBottom:5,paddingBottom:4,borderBottom:`1px solid ${T.divider}`}}>{s.text}</div>
                      : <p key={i} style={{margin:"0 0 10px 0",color:T.textSub}}>{s.text}</p>
                  )}
                </>
              ):(
                <div style={{color:T.textMuted,textAlign:"center",paddingTop:40}}>Loading waiver content…</div>
              )}
            </div>

            {/* Agreement checkbox */}
            <div ref={setRef("waiver")}>
              <label style={{
                display:"flex",alignItems:"flex-start",gap:13,cursor:"pointer",
                marginBottom:12,padding:"13px 16px",borderRadius:10,
                background:form.waiver_agreed?T.greenBg:isDark?"rgba(255,255,255,0.03)":"rgba(3,78,123,0.03)",
                border:`1.5px solid ${form.waiver_agreed?T.green+"55":errors.waiver?T.red+"60":T.glassBdr.replace("1px solid ","")}`,
                transition:"all 0.2s",
              }}>
                <input type="checkbox" checked={form.waiver_agreed} onChange={e=>set("waiver_agreed",e.target.checked)}
                  style={{marginTop:2,width:17,height:17,accentColor:T.green,flexShrink:0}}/>
                <span style={{fontSize:13,color:T.textSub,lineHeight:1.7}}>
                  I, <strong style={{color:T.textPrimary}}>{form.parent_name||"[Parent/Guardian Name]"}</strong>, on behalf of{" "}
                  <strong style={{color:T.textPrimary}}>{form.first_name?`${form.first_name} ${form.last_name}`:"[Student Name]"}</strong>,
                  {" "}have read and agree to all terms of the waiver above.
                  I confirm this electronic signature is legally binding.
                </span>
              </label>
              {errMsg("waiver")}
            </div>

            {/* Signature pad */}
            <div>
              <div style={{fontSize:11,fontWeight:700,color:T.textLabel,marginBottom:9,
                           display:"flex",alignItems:"center",gap:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>
                <FaPen size={10}/>
                Signature{!signature&&<span style={{color:T.red}}>*</span>}
                {signature&&<span style={{color:T.green,textTransform:"none",letterSpacing:0,fontWeight:700}}>✓ Signed</span>}
                <span style={{fontWeight:400,textTransform:"none",color:T.textMuted,letterSpacing:0,fontSize:10}}>
                  (Draw with mouse or finger · blue ink)
                </span>
              </div>
              <SigCanvas
                T={T}
                canvasRef={sigCanvasRef}
                onSign={data=>{setSignature(data);setErrors(e=>({...e,signature:""}));}}
                onClear={()=>setSignature("")}
                hasError={!!errors.signature}
              />
              {errMsg("signature")}
            </div>
          </div>

          {/* ── Submit ── */}
          <button type="submit" disabled={submitting}
            style={{
              width:"100%",padding:"16px",border:"none",borderRadius:12,
              fontSize:15,fontWeight:800,cursor:submitting?"not-allowed":"pointer",
              background:submitting?isDark?"rgba(255,255,255,0.08)":"rgba(3,78,123,0.15)":`linear-gradient(135deg,${getBrandColors().yellow} 0%,#e8b800 100%)`,
              color:submitting?T.textMuted:getBrandColors().blue,
              display:"flex",alignItems:"center",justifyContent:"center",gap:10,
              boxShadow:submitting?"none":"0 4px 24px rgba(247,206,11,0.4)",
              marginTop:6,transition:"all 0.15s",letterSpacing:"0.04em",
            }}>
            {submitting?"Submitting…":<><FaChevronRight size={13}/> Submit Registration</>}
          </button>
          <div style={{textAlign:"center",fontSize:11,color:T.textMuted,marginTop:10,marginBottom:8}}>
            Your information is kept confidential and used only for academy records.
          </div>
        </form>
      </div>
    </div>
  );
}

const thSt = T => ({
  padding:"9px 11px",fontWeight:800,fontSize:11,color:T.textMuted,
  textTransform:"uppercase",letterSpacing:"0.05em",
  borderBottom:`1px solid ${T.divider}`,whiteSpace:"nowrap",
});
const tdSt = T => ({
  padding:"13px 11px",borderBottom:`1px solid ${T.divider}`,verticalAlign:"middle",
});

export default function RegistrationForm(props) {
  return (
    <ThemeProvider>
      <RegistrationFormInner {...props}/>
    </ThemeProvider>
  );
}
